import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  deleteField,
  arrayRemove,
  serverTimestamp,
} from "../../services/FileHubWorkspaceSyncRepository";
import { deleteObject, getDownloadURL, uploadBytes } from "firebase/storage";
import { arrayMove } from "@dnd-kit/sortable";
import { FIREBASE_CONFIG } from "../../config/Config";
import {
  createWorkspaceDocRef,
  subscribeWorkspaceDoc,
  saveWorkspaceDoc,
  saveWorkspaceDocWithHistory,
  patchWorkspaceDoc,
  fetchWorkspaceDoc,
} from "../../services/FileHubWorkspaceSyncRepository";
import { fetchTemplate } from "../../services/FileHubTemplateRepository";
import useOrganizerStore from "../../store/useOrganizerStore";
import { insertAttachmentCitation } from "../../services/WordDocumentService";
import { sanitizeEvidenceName } from "../../utils/formatters";
import { executarComLimite } from "../../utils/executarComLimite";
import {
  numeroDoAnexo,
  numeroNoNome,
  rotuloDoSlot,
  rotulosPorSlot,
} from "../../utils/numeracaoAnexos";
import { doc, setDoc } from "firebase/firestore";
import { db } from "../../shared/api/firebase/config";
import { fetchClientDoc } from "../../services/FileHubCacheRepository";
import {
  CLIENT_TYPES,
  hydrateAndBuildCanonicalClientQualification,
} from "../../config/ClientSchema";
import {
  fetchGedLink,
  saveGedLink,
  addClientNote,
} from "../../services/FileHubDriveLinkRepository";
import { createOfficeNotification } from "../../services/NotificationService";
import {
  CHECKLISTS,
  PINNED_PIECE_TYPES,
} from "../../constants/autosChecklists";
import {
  UNIVERSAL_ROOT_DOCS,
  DOCUMENTOS_INSTRUCAO,
  CIVIL,
  PENAL,
  TRABALHO,
  PREVIDENCIARIO,
  TRIBUTARIO,
  ADMINISTRATIVO,
  resolveCadernoDocumentos,
  resolveCadernoDocumentosV3,
} from "../../config/LegalTaxonomy";
import { fetchWithWarmup, warmUpBackend } from "../../utils/backendWarmup";
import { computeFileSHA256 } from "../../utils/cryptoUtils";
import {
  checkDuplicatePetition,
  createPetitionIntent,
  commitPetition,
  addPetitionNote,
  rollbackPetition,
} from "../../services/FileHubPetitionRepository";
import { useFileHubDrive } from "../../hooks/useFileHubDrive";
import { useFileHubCache } from "../../hooks/useFileHubCache";
import { useFileHubSearch } from "../../hooks/useFileHubSearch";
import { useFileHubPartyPicker } from "../../hooks/useFileHubPartyPicker";
import { useFileHubChecklist } from "../../hooks/useFileHubChecklist";
import { useFileHubMeta } from "../../hooks/useFileHubMeta";
import { useFileHubPinned } from "../../hooks/useFileHubPinned";
import { useFileHubModals } from "../../hooks/useFileHubModals";
import { useFileHubFeedback } from "../../hooks/useFileHubFeedback";
import { useWorkspaceSignatures } from "../../services/SignatureService";

const saveJson = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {}
};

const loadJson = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {}
  return null;
};

const getBaseUrl = () => {
  const envUrl = String(import.meta?.env?.VITE_BACKEND_URL || "").trim();
  if (envUrl) return envUrl.replace(/\/+$/, "");
  // Hardcode intencional: VITE_BACKEND_URL no Render está fixado na URL antiga morta
  // (rda-backend.onrender.com sem -thuq). Ignoramos a env var para garantir URL correta.
  return "https://rda-backend-thuq.onrender.com";
};

const appId = FIREBASE_CONFIG.projectId;

const TAXONOMY_OPTGROUPS = [
  {
    label: "Documentos Universais",
    options: UNIVERSAL_ROOT_DOCS.map((d) => d.label),
  },
  {
    label: "Documentos de Instrução",
    options: DOCUMENTOS_INSTRUCAO.map((d) => d.label),
  },
  {
    label: "Direito Civil",
    options: Object.values(CIVIL.fases).flatMap((f) =>
      f.pecas.map((p) => p.label),
    ),
  },
  {
    label: "Direito Penal",
    options: Object.values(PENAL.fases).flatMap((f) =>
      f.pecas.map((p) => p.label),
    ),
  },
  {
    label: "Direito do Trabalho",
    options: Object.values(TRABALHO.fases).flatMap((f) =>
      f.pecas.map((p) => p.label),
    ),
  },
  {
    label: "Direito Previdenciário",
    options: Object.values(PREVIDENCIARIO.fases).flatMap((f) =>
      f.pecas.map((p) => p.label),
    ),
  },
  {
    label: "Direito Tributário",
    options: Object.values(TRIBUTARIO.fases).flatMap((f) =>
      f.pecas.map((p) => p.label),
    ),
  },
  {
    label: "Direito Administrativo",
    options: Object.values(ADMINISTRATIVO.fases).flatMap((f) =>
      f.pecas.map((p) => p.label),
    ),
  },
];
// MC59 (03/09/2026, pedido do titular): "Documentos Universais" e
// "Documentos de Instrução" são DOCUMENTOS/ANEXOS (Procuração, RG,
// Comprovante de Residência, Depósito Recursal, etc.) — nunca uma PEÇA em
// si (o que se protocola no processo). TAXONOMY_OPTGROUPS os inclui
// porque o mesmo dado alimenta o checklist de anexos em outro lugar
// (correto ali); aqui, no dropdown de TIPO DA PEÇA, eles nunca deveriam
// aparecer como opção — só os ramos de direito (Civil, Penal, Trabalho,
// Previdenciário, Tributário, Administrativo), que são petições de
// verdade. Filtrados pelo label dos 2 grupos, não removidos da fonte.
const PIECE_OPTGROUPS_V2 = TAXONOMY_OPTGROUPS.filter(
  (group) =>
    group.label !== "Documentos Universais" &&
    group.label !== "Documentos de Instrução",
);

function parseAITextToChecklist(text) {
  if (!text || typeof text !== "string") return [];
  const lines = text.split(/\r?\n/);
  const results = [];
  for (const rawLine of lines) {
    let line = rawLine.trim();
    if (!line || line.length < 3) continue;
    line = line.replace(
      /^(\d+[\.\)\-]\s*|[a-zA-Z]\)\s*|[ivxlIVXL]+[\.\)\-]\s*)/,
      "",
    );
    line = line.replace(/^[\-\*\u2022\u2013\u2014\u2192\u25CF\u25E6]+\s*/, "");
    line = line.trim();
    if (!line || line.length < 3) continue;
    const colonIdx = line.indexOf(":");
    if (colonIdx > 2) line = line.substring(0, colonIdx);
    line = line.replace(/[.;,]+$/, "").trim();
    if (line.length >= 3 && line.length <= 120) results.push(line);
  }
  return [...new Map(results.map((r) => [r.toLowerCase(), r])).values()];
}

function extractDriveFolderId(v) {
  if (!v) return "";
  const s = String(v).trim();
  if (s.length >= 20 && !s.includes("/")) return s;
  try {
    const u = new URL(s);
    const p = u.pathname;
    const m = p.match(/\/folders\/([a-zA-Z0-9_-]+)/);
    if (m) return m[1];
  } catch {}
  return s;
}

const stripHtmlTags = (s) => String(s || "").replace(/<[^>]+>/g, "");

const escapeXml = (s) => {
  return String(s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
};

const wrapInOOXML = (plainText) => {
  const text = String(plainText || "");
  const ns = "http://schemas.openxmlformats.org/wordprocessingml/2006/main";
  const lines = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n");
  if (!lines.length) {
    return `<w:p xmlns:w="${ns}"><w:r><w:t xml:space="preserve"></w:t></w:r></w:p>`;
  }
  return lines
    .map((line, idx) => {
      const safe = escapeXml(line);
      const lead = idx === 0 ? `<w:p xmlns:w="${ns}">` : "<w:p>";
      return `${lead}<w:r><w:t xml:space="preserve">${safe}</w:t></w:r></w:p>`;
    })
    .join("");
};

const getExtFromName = (fileName) => {
  const m = String(fileName || "").match(/\.(\w{2,5})$/i);
  return m ? `.${m[1].toLowerCase()}` : ".pdf";
};

const ensureFileExt = (name, originalExt) => {
  const t = String(name || "").trim();
  const ext = String(originalExt || ".pdf").toLowerCase();
  if (!t) return `Arquivo${ext}`;
  if (t.toLowerCase().endsWith(ext)) return t;
  return `${t}${ext}`;
};

const ensurePdfExt = (name, originalExt) => ensureFileExt(name, originalExt);

// CAMADA 9 (03/09/2026, PROMPT_TCREI_LOOP_INFINITO_TROCA_PECA — recorrência
// pós-restauração PITR): `JSON.stringify` de um array de objetos é sensível
// à ORDEM DE INSERÇÃO DAS CHAVES de cada objeto, não só ao conteúdo. Achado
// ao vivo (log [INSTR-ADVSIG]): o mesmo objeto de parte adversária
// (activeWorkspace.partes.adversas) chegava com chaves em ordens diferentes
// entre disparos consecutivos do onSnapshot do workspace (provavelmente 2
// fontes/serializações diferentes do "mesmo" dado, nunca confirmado qual —
// não importa pra este fix, que é defensivo contra QUALQUER fonte que faça
// isso). `adversasSignature` (usado como dependência do efeito Bridge)
// produzia uma STRING NOVA a cada disparo mesmo com o conteúdo
// semanticamente idêntico — reabrindo exatamente o mesmo tipo de loop das
// Camadas 1-8, por um mecanismo novo. `stableStringify` ordena as chaves de
// todo objeto (recursivo) antes de serializar — duas cópias do mesmo dado,
// em qualquer ordem de chave, sempre produzem a mesma string.
const stableStringify = (value) => {
  const sorter = (val) => {
    if (Array.isArray(val)) return val.map(sorter);
    if (val && typeof val === "object") {
      return Object.keys(val)
        .sort()
        .reduce((acc, k) => {
          acc[k] = sorter(val[k]);
          return acc;
        }, {});
    }
    return val;
  };
  return JSON.stringify(sorter(value));
};

const partyRefsEqual = (a, b) => {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    const ai = a[i] || {};
    const bi = b[i] || {};
    if (String(ai.clientId || "") !== String(bi.clientId || "")) return false;
    if (String(ai.role || "") !== String(bi.role || "")) return false;
  }
  return true;
};

const fetchWithTimeout = (url, options = {}, timeoutMs = 15000) => {
  const controller = new AbortController();
  const t = setTimeout(() => controller.abort(), timeoutMs);
  const merged = { ...(options || {}), signal: controller.signal };
  const promise = fetch(url, merged).finally(() => clearTimeout(t));
  return { controller, promise };
};

const LS_ACTIVE_DRIVE_FOLDER = "rda_active_drive_folder";
const LS_AUTOS_FOLDER = "rda_autos_folder";


// Quantas renomeações no Drive podem acontecer ao mesmo tempo no Salvar & Organizar (U26).
const LIMITE_RENOMEACOES_SIMULTANEAS = 5;

const STOP_WORDS_PT = new Set([
  "de",
  "do",
  "da",
  "dos",
  "das",
  "em",
  "no",
  "na",
  "nos",
  "nas",
  "por",
  "pelo",
  "pela",
  "pelos",
  "pelas",
  "com",
  "para",
  "a",
  "ao",
  "aos",
  "e",
  "ou",
  "que",
  "se",
  "o",
  "os",
  "as",
  "um",
  "uma",
  "uns",
  "umas",
]);

const ACRONYMS_PT = new Set([
  "SJP",
  "DOU",
  "CONAB",
  "PAD",
  "RH",
  "SEED",
  "PR",
  "SP",
  "RJ",
  "MG",
  "RS",
  "SC",
  "TJ",
  "STJ",
  "STF",
  "CNJ",
  "OAB",
  "INSS",
  "PJE",
  "TJPR",
  "TJSC",
  "TJSP",
  "CPC",
  "CPP",
  "CF",
  "CLT",
  "ECA",
  "CDC",
  "CTN",
  "PF",
  "PJ",
  "MS",
  "HC",
  "ADPF",
  "ADI",
  "NPU",
  "TST",
  "TRF",
  "TRE",
  "MPF",
  "AGU",
  "IBGE",
]);

const toTitleCaseABNT = (text) => {
  let afterParen = false;
  return text
    .split(" ")
    .map((word, idx) => {
      if (!word) return word;
      if (word.startsWith("(")) {
        const inner = word.slice(1);
        if (!inner) {
          afterParen = true;
          return word;
        }
        const up = inner.toUpperCase();
        if (ACRONYMS_PT.has(up)) return "(" + up;
        return (
          "(" + inner.charAt(0).toUpperCase() + inner.slice(1).toLowerCase()
        );
      }
      const upper = word.toUpperCase();
      if (ACRONYMS_PT.has(upper.replace(/[^A-Z]/g, ""))) return upper;
      if (/^\d/.test(word)) return word;
      if (
        /^[A-Z]+\/[A-Z]+$/.test(word) &&
        word.split("/").every((p) => ACRONYMS_PT.has(p))
      )
        return word;
      const lower = word.toLowerCase();
      if (idx > 0 && !afterParen && STOP_WORDS_PT.has(lower)) return lower;
      afterParen = false;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
};

const KIT_DEFAULT = [
  "Procuração Ad Judicia",
  "Documento de Identificação (RG / CNH)",
  "Comprovante de Residência",
  "Contrato de Honorários Advocatícios",
  "Termo de Consentimento LGPD",
];

const _KIT_INICIAL_CIVEL = [
  "Procuração Ad Judicia",
  "Documento de Identificação (RG / CNH)",
  "Comprovante de Residência",
  "Contrato de Honorários Advocatícios",
  "Pedido de Tutela de Urgência",
];
const _KIT_CONTESTACAO = [
  "Procuração Ad Judicia",
  "Documento de Identificação (RG / CNH)",
  "Contrato de Honorários Advocatícios",
  "Réplica / Impugnação à Contestação",
  "Petição de Especificação de Provas",
];
const _KIT_RESPOSTA_ACUSACAO = [
  "Procuração Ad Judicia",
  "Documento de Identificação (RG / CNH)",
  "Queixa-Crime",
  "Memoriais / Alegações Finais",
  "Rol de Testemunhas",
];
const _KIT_HC = [
  "Procuração Ad Judicia",
  "Documento de Identificação (RG / CNH)",
  "Auto de Prisão em Flagrante",
  "Pedido de Revogação de Prisão Preventiva",
  "Comprovante de Residência",
];
const _KIT_TRABALHISTA = [
  "Procuração Ad Judicia",
  "Documento de Identificação (RG / CNH)",
  "Comprovante de Residência",
  "Contrato de Honorários Advocatícios",
  "Declaração de Hipossuficiência",
];
const _KIT_PREVIDENCIARIO = [
  "Procuração Ad Judicia",
  "Documento de Identificação (RG / CNH)",
  "Comprovante de Residência",
  "Dossiê Médico e CNIS",
  "Petição de Cumprimento de Exigência",
];

const PIECE_KITS = {
  "Petição Inicial (Cível)": _KIT_INICIAL_CIVEL,
  "Resposta à Acusação (Penal)": _KIT_RESPOSTA_ACUSACAO,
  "Contestação (Cível)": _KIT_CONTESTACAO,
  "Habeas Corpus": _KIT_HC,
  "Ação Trabalhista": _KIT_TRABALHISTA,
  "Ação Previdenciária": _KIT_PREVIDENCIARIO,
};

const getChecklistDefault = (type) => {
  const v3docs = resolveCadernoDocumentosV3(type);
  if (v3docs.length > 0) return v3docs.map((d) => d.label);
  const v2docs = resolveCadernoDocumentos(type);
  if (v2docs.length > 0) return v2docs.map((d) => d.label);
  const found = CHECKLISTS[type];
  if (Array.isArray(found) && found.length) return found;
  const kitFallback = PIECE_KITS[type];
  if (Array.isArray(kitFallback) && kitFallback.length) return kitFallback;
  return [
    "Procuração",
    "Documento de Identificação",
    "Comprovante de Endereço",
    "Outros",
  ];
};

const toSafeBase = (raw) => {
  const t = String(raw || "").trim();
  if (!t) return "Arquivo";
  return (
    t
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(
        /\.(pdf|mp3|mp4|m4a|opus|ogg|wav|jpg|jpeg|png|gif|webp|docx?|xlsx?|pptx?|zip|rar)$/i,
        "",
      )
      .replace(/[()[\]{}'"]/g, "")
      .replace(/[/\\|+&@#%=,:;!?]/g, "-")
      .replace(/\s+/g, "_")
      .replace(/[^\w\-]/g, "")
      .replace(/-{2,}/g, "-")
      .replace(/_{2,}/g, "_")
      .replace(/^[-_]+|[-_]+$/g, "")
      .toUpperCase() || "Arquivo"
  );
};

export default function useFileHub({
  activeWorkspace,
  activePieceId,
  initialFolderId,
  autoConnect = false,
  onBack,
  onHostBridge,
}) {
  const isWorkspaceControlled = Boolean(activeWorkspace && activeWorkspace.id);

  // FIX ESTRUTURA CADERNO > PEÇAS (21/08/2026): um caderno (workspace) pode
  // ter várias peças (Denúncia, Apelação, etc.), e agora cada peça tem sua
  // PRÓPRIA pasta/Anexos no Drive — não mais uma pasta única compartilhada
  // por todas as peças do caderno (isso fazia trocar de peça "sobrescrever"
  // a pasta anterior, porque era literalmente a mesma pasta sendo renomeada).
  // Esta é a resolução central: sempre que o código precisar da pasta de
  // anexos "da peça atual", ele deve ler daqui — nunca direto de
  // activeWorkspace.processPieceFolderId/attachmentsFolderId, que agora só
  // servem de fallback para cadernos antigos criados antes desta mudança
  // (uma única peça, sem array `pieces` com pastas próprias).
  const activePieceRecordId = String(activePieceId || "").trim();
  const activePieceRecord = (() => {
    const list = Array.isArray(activeWorkspace?.pieces)
      ? activeWorkspace.pieces
      : [];
    if (!activePieceRecordId) return null;
    return list.find((p) => String(p?.id || "") === activePieceRecordId) || null;
  })();
  const resolvedPieceFolderId = String(
    activePieceRecord?.processPieceFolderId ||
      activeWorkspace?.processPieceFolderId ||
      "",
  ).trim();
  const resolvedAttachmentsFolderId = String(
    activePieceRecord?.attachmentsFolderId ||
      activeWorkspace?.attachmentsFolderId ||
      "",
  ).trim();
  // Trava de rename por pasta (não por caderno inteiro) — ver
  // renamePieceFolderOnDrive mais abaixo.
  const pieceRenameInFlightRef = useRef(new Set());

  // ── Estado base ───────────────────────────────────────────────────────────
  const [pendingAssign, setPendingAssign] = useState(null);
  const [syncBusy, setSyncBusy] = useState(false);
  const [chaosBusy, setChaosBusy] = useState(false);
  const [petitionBusy, setPetitionBusy] = useState(false);
  const [hostMode, setHostMode] = useState("web");
  const [uiWidthMode, setUiWidthMode] = useState(() => {
    try {
      return String(
        localStorage.getItem("rda_taskpane_width_mode") || "compact",
      );
    } catch {
      return "compact";
    }
  });
  const [workspaceHydratedKey, setWorkspaceHydratedKey] = useState("");
  const [isRenaming, setIsRenaming] = useState(false);
  // ITEM C (26/08/2026): progresso "X de Y" durante applyRenameOnDrive, pra
  // tela de travamento/progresso do "Salvar & Organizar" mostrar algo
  // concreto, não só um spinner genérico.
  const [renameProgress, setRenameProgress] = useState({ done: 0, total: 0 });
  const [isEdgeProcessing, setIsEdgeProcessing] = useState(false);
  const [uploadLocks, setUploadLocks] = useState({});
  const [workspacePartyRefs, setWorkspacePartyRefs] = useState(() => {
    const raw = Array.isArray(activeWorkspace?.partiesRefs)
      ? activeWorkspace.partiesRefs
      : [];
    return raw
      .map((p) => {
        if (typeof p === "string")
          return p.trim()
            ? { clientId: p.trim(), role: "Parte", name: "", resp: "" }
            : null;
        return {
          clientId: String(p?.clientId || "").trim(),
          role: String(p?.role || "").trim(),
          name: String(p?.name || "").trim(),
          resp: String(p?.resp || "").trim(),
        };
      })
      .filter((p) => p && p.clientId);
  });
  const [partyDetails, setPartyDetails] = useState([]);
  const [bridgeForceCounter, setBridgeForceCounter] = useState(0);
  const [pieceType, setPieceType] = useState(
    () =>
      String(activeWorkspace?.pieceType || "").trim() ||
      "Petição Inicial (Cível)",
  );
  const [billingModal, setBillingModal] = useState(false);
  const [nukeOpen, setNukeOpen] = useState(false);
  const [nukeText, setNukeText] = useState("");
  const [driveFolderInput, _setDriveFolderInput] = useState(() => {
    try {
      if (isWorkspaceControlled) {
        const raw = String(
          activeWorkspace?.driveFolderInput ||
            activeWorkspace?.driveFolderId ||
            "",
        ).trim();
        if (raw) return raw;
      }
      return "";
    } catch {
      return "";
    }
  });
  const setDriveFolderInput = useCallback((v) => {
    const next = typeof v === "function" ? v(driveFolderInputRef.current) : v;
    driveFolderInputRef.current = next;
    _setDriveFolderInput(next);
  }, []);

  // ── Derived / Memoized ──────────────────────────────────────────────────
  void PINNED_PIECE_TYPES; // evita tree-shake
  const isMirrorMode = hostMode === "word";
  const systemInitialFolderId = useMemo(
    () => extractDriveFolderId(initialFolderId),
    [initialFolderId],
  );
  const zeroClickActive = Boolean(autoConnect && systemInitialFolderId);
  const isDev = Boolean(
    typeof import.meta !== "undefined" &&
    import.meta?.env &&
    import.meta.env.DEV,
  );
  const workspaceKey = useMemo(() => {
    if (isWorkspaceControlled) return String(activeWorkspace?.id || "").trim();
    return "";
  }, [isWorkspaceControlled, activeWorkspace?.id]);
  const workspaceDocRef = useMemo(() => {
    if (!workspaceKey) return null;
    return createWorkspaceDocRef(appId, workspaceKey);
  }, [workspaceKey]);
  const effectivePieceKey = String(activePieceId || "").trim();

  // ── Hooks menores (devem vir antes de qualquer useMemo que os use) ──────
  const drive = useFileHubDrive();
  const cache = useFileHubCache();
  const search = useFileHubSearch();
  const partyPicker = useFileHubPartyPicker();
  // MC56 (03/09/2026, achado real: log infinito só na Contestação, nunca
  // na Petição Inicial) — useFileHubChecklist recebia activeWorkspace
  // inteiro e só calculava o "template" UMA VEZ, na 1ª montagem, usando
  // activeWorkspace.pieceType (campo do CADERNO, legado, sempre a 1ª
  // peça). Trocar de peça no MESMO caderno nunca reavaliava. O "checklist"
  // resultante (5 itens da Contestação) ficava sistematicamente
  // dessincronizado do slotOrder salvo (34 itens, poluído pela mesma
  // causa raiz já corrigida antes) — outros efeitos que comparam
  // checklist.checklist contra slotOrder ("expected") nunca convergiam,
  // daí o loop. Passa o pieceType da PEÇA ATIVA de verdade.
  const activePieceTypeForChecklist =
    String(activePieceRecord?.pieceType || "").trim() || undefined;
  const checklist = useFileHubChecklist(
    activeWorkspace,
    activePieceTypeForChecklist,
  );
  const meta = useFileHubMeta(activeWorkspace);
  const pinned = useFileHubPinned();
  const modals = useFileHubModals();
  const fb = useFileHubFeedback();
  const { signatures } = useWorkspaceSignatures(workspaceKey);
  const listDriveFolderRef = useRef(null);
  const syncWorkspaceNowRef = useRef(null);
  const clientDataRef = useRef({
    clientId: "",
    clientName: "",
    clientCpf: "",
    clientEmail: "",
  });

  const setClientData = useCallback((data) => {
    clientDataRef.current = {
      clientId: String(data?.clientId || "").trim(),
      clientName: String(data?.clientName || "").trim(),
      clientCpf: String(data?.clientCpf || "").trim(),
      clientEmail: String(data?.clientEmail || "").trim(),
    };
    // FIX V23: Incrementa contador para forçar re-envio do POST /bridge/state
    // quando o CRM publica alteracoes. O watcher da bridge usa bridgeForceCounter
    // como dependencia em vez de partyDetails para evitar race condition.
    setBridgeForceCounter((prev) => prev + 1);
  }, []);

  const stateKey = useMemo(() => {
    if (!drive.driveFolderId) return "";
    if (!effectivePieceKey) return "";
    return `rda_autos_state_${drive.driveFolderId}_${effectivePieceKey}`;
  }, [drive.driveFolderId, effectivePieceKey]);
  const currentRamoDocs = useMemo(() => {
    if (!pieceType) return null;
    for (const group of TAXONOMY_OPTGROUPS) {
      if (group.options.includes(pieceType))
        return { label: group.label, options: group.options };
    }
    return null;
  }, [pieceType]);

  // ── Organizer Store (Zustand) ────────────────────────────────────────────
  const assignments = useOrganizerStore((s) => s.assignments);
  const setAssignments = useOrganizerStore((s) => s.setAssignments);
  const slotNames = useOrganizerStore((s) => s.slotNames);
  const setSlotNames = useOrganizerStore((s) => s.setSlotNames);
  const slotOrder = useOrganizerStore((s) => s.slotOrder);
  const setSlotOrder = useOrganizerStore((s) => s.setSlotOrder);
  const isHydrated = useOrganizerStore((s) => s.isHydrated);
  const setHydratedState = useOrganizerStore((s) => s.setHydratedState);
  const resetOrganizerStore = useOrganizerStore((s) => s.reset);
  const clientRequested = useOrganizerStore((s) => s.clientRequested);
  const setClientRequested = useOrganizerStore((s) => s.setClientRequested);
  const revertState = useOrganizerStore((s) => s.revertState);
  // Numeração dos anexos (24/09/2026): "começar em N" da peça (padrão 1). Viaja com
  // os 4 campos acima em TODO caminho de salvar/hidratar/reverter (ver comentário na store).
  const numeroInicialAnexos = useOrganizerStore((s) => s.numeroInicialAnexos);
  const setNumeroInicialAnexos = useOrganizerStore((s) => s.setNumeroInicialAnexos);
  // Rótulos PRONTOS por slot ("ANEXO 07" / "ANEXO 007"), vazios incluídos. É o que a ponte
  // (/bridge/state) manda ao Word: o Word é só espelho e IMPRIME isto, nunca calcula.
  const rotulosDosAnexos = useMemo(
    () => rotulosPorSlot(slotOrder, numeroInicialAnexos),
    [slotOrder, numeroInicialAnexos],
  );
  const markHydrated = useOrganizerStore((s) => s.markHydrated);
  // FIX FLICKER CADERNO DE AUTOS (22/08/2026) — nome deliberadamente
  // diferente de workspaceHydratedKey/setWorkspaceHydratedKey (useState
  // local, linha ~515 — este morre no remount, é ESSE o bug). Este campo
  // vive na store Zustand (sobrevive ao remount de FileHubArea).
  const hydratedWorkspaceKeyStore = useOrganizerStore((s) => s.hydratedWorkspaceKey);
  const setHydratedWorkspaceKeyStore = useOrganizerStore((s) => s.setHydratedWorkspaceKey);

  // ── Refs ────────────────────────────────────────────────────────────────
  const partyOmniboxRef = useRef(null);
  const linkOmniboxRef = useRef(null);
  const workspaceHydratedKeyRef = useRef("");
  // FIX PERDA DE DADO AO TROCAR DE PEÇA (21/08/2026): workspaceHydratedKeyRef
  // só trava por workspaceKey (o CADERNO) — a assinatura onSnapshot do
  // Firestore roda a "primeira hidratação" (com toda a lógica de
  // recuperação de assignments) só UMA VEZ por caderno, nunca de novo ao
  // trocar de PEÇA dentro do mesmo caderno. Se essa primeira hidratação
  // acontecesse com effectivePieceKey ainda vazio/errado (ex.: logo depois
  // de um reload, antes de activePieceId propagar), a peça real nunca
  // recebia seus assignments verdadeiros — e o efeito de auto-save (que só
  // depende de isHydrated, não de "hidratado para ESTA peça") gravava o
  // estado local vazio por cima do Firestore, apagando o vínculo real.
  // pieceHydratedKeyRef rastreia workspaceKey+effectivePieceKey juntos, pra
  // a hidratação completa (com recuperação) rodar de novo sempre que a
  // peça ativa mudar, não só quando o caderno muda.
  const pieceHydratedKeyRef = useRef("");
  const lastConfirmedOrgStateRef = useRef(null);
  const metaJurisdictionRef = useRef(null);
  const noticeTimerRef = useRef(null);
  const renameTimerBySlotRef = useRef({});
  // FIX GATILHO AUTOMÁTICO 3 (26/08/2026): substitui o autosave de 700ms —
  // só marca que existe edição não persistida, para o aviso de beforeunload.
  // Zerado em syncWorkspaceNow() após gravação bem-sucedida.
  const hasUnsavedChangesRef = useRef(false);
  const driveListAbortRef = useRef(null);
  const driveFolderInputRef = useRef("");
  const bridgePostTimerRef = useRef(null);
  const autoConnectKeyRef = useRef("");
  // CAMADA 8 (03/09/2026, PROMPT_TCREI_LOOP_INFINITO_TROCA_PECA_SEGUNDA_
  // OPINIAO): ref SEPARADO para o 2º efeito de auto-connect (fallback pra
  // systemInitialFolderId) — ver os 2 efeitos logo abaixo de
  // connectToDriveFolder, marcador CAMADA-8, para o porquê de precisar ser
  // separado do autoConnectKeyRef acima.
  const autoConnectInitialKeyRef = useRef("");
  const localOrganizerUpdatedAtRef = useRef(0);
  const clientRequestedInitKeyRef = useRef("");
  const clientRequestedSyncRef = useRef(false);
  const clientRequestedSyncKeyRef = useRef("");
  const partySubscriptionConfirmedRef = useRef(false);
  const resetGuardWorkspaceKeyRef = useRef(null);
  // CAMADA 6 (03/09/2026, PROMPT_TCREI_LOOP_INFINITO_TROCA_PECA_SEGUNDA_OPINIAO):
  // assinatura do último partiesRefs efetivamente GRAVADO no Firestore — ver
  // o efeito de write-back logo abaixo, marcador CAMADA-6.
  const lastSavedPartiesRefsSigRef = useRef("");

  // FIX FLICKER CADERNO DE AUTOS (22/08/2026) — reset SÍNCRONO, durante a
  // renderização (não useEffect), seguindo o padrão que o próprio React
  // recomenda pra "resetar estado quando uma chave muda" (react.dev/learn/
  // you-might-not-need-an-effect) e que a comunidade do Zustand confirma
  // pra stores globais (github.com/pmndrs/zustand/discussions/2648).
  // Causa raiz do bug: o Zustand É global e sobrevive ao remount de
  // FileHubArea (troca de aba); antes, nada detectava de forma síncrona
  // "esse assignments/slotOrder na tela pertence a um caderno DIFERENTE do
  // activeWorkspace atual" — só o efeito de hidratação (assíncrono, useRef
  // que morre no remount) corrigia, depois de já ter pintado o dado velho.
  // resetGuardWorkspaceKeyRef (local) só evita chamar reset() em todo
  // re-render enquanto a hidratação assíncrona ainda não chegou — quem
  // decide "já hidratei esse caderno mesmo depois de um remount" é
  // hydratedWorkspaceKeyStore (na store, sobrevive ao remount).
  if (
    workspaceKey &&
    hydratedWorkspaceKeyStore !== workspaceKey &&
    resetGuardWorkspaceKeyRef.current !== workspaceKey
  ) {
    resetGuardWorkspaceKeyRef.current = workspaceKey;
    resetOrganizerStore();
  }

  // FIX FLICKER CADERNO DE AUTOS, 2ª causa raiz (22/08/2026) — achada
  // depois que o titular testou o fix acima e reportou que o problema
  // continuava: o nome errado que aparece por ~5s não vem do
  // useOrganizerStore (Firestore, rápido) — vem de drive.driveFiles
  // (useFileHubDrive.js, useState LOCAL), que guarda a listagem real do
  // Google Drive. Ela não é limpa quando o CADERNO muda porque
  // connectToDriveFolder roda com resetState:false nos dois efeitos de
  // auto-connect (linhas ~4086/4117) — deliberado, pra não apagar
  // assignments ao trocar só de PEÇA dentro do mesmo caderno (mesma
  // pasta de Drive por peça). Só que ao trocar de CADERNO a pasta do
  // Drive é sempre outra, e a listagem antiga (de outro cliente/processo)
  // fica na tela até o Google responder (~5s, latência real da API,
  // fora do nosso controle). Reset síncrono aqui, só de driveFiles — não
  // mexe em resetState nem nos efeitos de auto-connect, então o
  // comportamento de troca de PEÇA (que motivou resetState:false) continua
  // intacto.
  const driveFilesResetGuardRef = useRef(null);
  if (
    workspaceKey &&
    driveFilesResetGuardRef.current !== workspaceKey &&
    drive.driveFiles.length > 0
  ) {
    driveFilesResetGuardRef.current = workspaceKey;
    drive.setDriveFiles([]);
  }

  // ── Derived sets / lists ────────────────────────────────────────────────
  const assignedIds = useMemo(() => {
    const ids = new Set();
    Object.values(assignments || {}).forEach((v) => {
      const mainId = String(v?.id || "").trim();
      const signedId = String(v?.signedDriveId || "").trim();
      const origId = String(v?.originalDriveId || "").trim();
      if (mainId) ids.add(mainId);
      if (signedId) ids.add(signedId);
      if (origId) ids.add(origId);
      if (Array.isArray(v?.multiFiles))
        v.multiFiles.forEach((mf) => {
          const mfId = String(mf?.id || "").trim();
          if (mfId) ids.add(mfId);
        });
    });
    return ids;
  }, [assignments]);

  const orderedDriveFiles = useMemo(() => {
    if (!drive.driveFiles?.length) return [];
    const assigned = [];
    const unassigned = [];
    const driveById = new Map(drive.driveFiles.map((f) => [String(f.id), f]));
    for (const slotId of slotOrder) {
      const fileId = assignments?.[slotId]?.id;
      if (!fileId) continue;
      const file = driveById.get(String(fileId));
      if (file) assigned.push(file);
    }
    const assignedIdsSet = new Set(assigned.map((f) => String(f.id)));
    Object.values(assignments || {}).forEach((v) => {
      const sid = String(v?.signedDriveId || "").trim();
      const oid = String(v?.originalDriveId || "").trim();
      if (sid) assignedIdsSet.add(sid);
      if (oid) assignedIdsSet.add(oid);
      if (Array.isArray(v?.multiFiles))
        v.multiFiles.forEach((mf) => {
          const mfId = String(mf?.id || "").trim();
          if (mfId) assignedIdsSet.add(mfId);
        });
    });
    for (const f of drive.driveFiles) {
      if (!assignedIdsSet.has(String(f.id))) unassigned.push(f);
    }
    return [...assigned, ...unassigned];
  }, [drive.driveFiles, assignments, slotOrder]);

  const pendingDriveFiles = useMemo(() => {
    const hidden =
      drive.triagedDriveFileIds instanceof Set
        ? drive.triagedDriveFileIds
        : new Set();
    return (Array.isArray(drive.driveFiles) ? drive.driveFiles : []).filter(
      (f) =>
        f &&
        String(f.id || "").trim() &&
        !assignedIds.has(String(f.id)) &&
        !hidden.has(String(f.id)),
    );
  }, [drive.driveFiles, assignedIds, drive.triagedDriveFileIds]);

  const selectedDriveFile = useMemo(() => {
    const id = String(drive.selectedDriveFileId || "").trim();
    return (
      (Array.isArray(drive.driveFiles) ? drive.driveFiles : []).find(
        (f) => String(f?.id || "").trim() === id,
      ) || null
    );
  }, [drive.driveFiles, drive.selectedDriveFileId]);

  const completion = useMemo(() => {
    const total = slotOrder.length;
    const filled = slotOrder.reduce(
      (acc, slotId) => (assignments?.[slotId] ? acc + 1 : acc),
      0,
    );
    return { total, filled };
  }, [assignments, slotOrder]);

  // ── Callbacks helpers ───────────────────────────────────────────────────
  const norm = useCallback((v) => {
    return String(v || "")
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
  }, []);

  const resolveClientName = useCallback((c) => {
    const t = String(c?.type || "").toUpperCase();
    if (t === "PESSOA FÍSICA" || t === "PHYSICAL")
      return c?.qualification?.physical?.name || c?.name || "Pessoa Física";
    if (t === "PESSOA JURÍDICA" || t === "PESSOA JURIDICA" || t === "LEGAL")
      return (
        c?.qualification?.legal?.companyName || c?.name || "Pessoa Jurídica"
      );
    return c?.name || "Registro";
  }, []);

  const resolveWorkspaceLabel = useCallback((w) => {
    const title =
      String(w?.pieceType || w?.title || "Processo").trim() || "Processo";
    const clientName = String(w?.clientName || "").trim();
    return clientName ? `${title} • ${clientName}` : title;
  }, []);

  const handleAIAnalyze = useCallback(() => {
    const parsed = parseAITextToChecklist(modals.aiWizardText);
    modals.setAiWizardItems(
      parsed.map((name, i) => ({ id: i, name, selected: true })),
    );
    modals.setAiWizardPhase("preview");
  }, [modals.aiWizardText, modals.setAiWizardItems, modals.setAiWizardPhase]);

  const handleAIToggleItem = useCallback(
    (id) => {
      modals.setAiWizardItems((prev) =>
        prev.map((it) =>
          it.id === id ? { ...it, selected: !it.selected } : it,
        ),
      );
    },
    [modals.setAiWizardItems],
  );

  const handleAIInjectSlots = useCallback(() => {
    const selected = modals.aiWizardItems.filter((it) => it.selected);
    if (!selected.length) return;
    const newIds = selected.map(
      () =>
        `slot-extra-ai-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    );
    setSlotOrder((prev) => [...(Array.isArray(prev) ? prev : []), ...newIds]);
    setSlotNames((prev) => {
      const next = { ...prev };
      selected.forEach((it, i) => {
        next[newIds[i]] = it.name;
      });
      return next;
    });
    modals.setAiWizardOpen(false);
    modals.setAiWizardText("");
    modals.setAiWizardItems([]);
  }, [
    modals.aiWizardItems,
    modals.setAiWizardOpen,
    modals.setAiWizardText,
    modals.setAiWizardItems,
    setSlotOrder,
    setSlotNames,
  ]);

  const loadClientsCache = useCallback(async () => {
    if (cache.clientsCacheLoading) return;
    if (Array.isArray(cache.clientsCache) && cache.clientsCache.length) return;
    cache.setClientsCacheLoading(true);
    try {
      const rows = await fetchClientsCache(appId);
      cache.setClientsCache(rows);
    } catch (e) {
      try {
        console.warn("[CRM] Falha ao carregar cache de clientes:", e);
      } catch {}
      cache.setClientsCache([]);
    } finally {
      cache.setClientsCacheLoading(false);
    }
  }, [cache.clientsCacheLoading, cache.clientsCache]);

  const loadWorkspacesCache = useCallback(async () => {
    if (cache.workspacesCacheLoading) return;
    if (Array.isArray(cache.workspacesCache) && cache.workspacesCache.length)
      return;
    cache.setWorkspacesCacheLoading(true);
    try {
      const rows = await fetchWorkspacesCache(appId);
      cache.setWorkspacesCache(rows);
    } catch (e) {
      try {
        console.warn("[Workspaces] Falha ao carregar cache:", e);
      } catch {}
      cache.setWorkspacesCache([]);
    } finally {
      cache.setWorkspacesCacheLoading(false);
    }
  }, [cache.workspacesCacheLoading, cache.workspacesCache]);

  const runClientSearch = useCallback(
    async (term, setter) => {
      const t = norm(term);
      if (!t) {
        setter([]);
        return;
      }
      await loadClientsCache();
      const base = Array.isArray(cache.clientsCache) ? cache.clientsCache : [];
      const out = base
        .map((c) => {
          const name = resolveClientName(c);
          const cpf = String(c?.cpf || c?.qualification?.physical?.cpf || "");
          const cnpj = String(c?.cnpj || c?.qualification?.legal?.cnpj || "");
          const email = String(
            c?.contact?.email || c?.contact?.emailPrimary || "",
          );
          const hay = norm(
            `${name} ${cpf} ${cnpj} ${email} ${c?.qualificationText || ""}`,
          );
          return hay.includes(t) ? { ...c, __displayName: name } : null;
        })
        .filter(Boolean)
        .slice(0, 40);
      setter(out);
    },
    [cache.clientsCache, loadClientsCache, norm, resolveClientName],
  );

  const runLinkSearch = useCallback(
    async (term) => {
      const t = norm(term);
      if (!t) {
        search.setLinkResults([]);
        return;
      }
      await loadWorkspacesCache();
      await loadClientsCache();
      const baseWs = Array.isArray(cache.workspacesCache)
        ? cache.workspacesCache
        : [];
      const baseClients = Array.isArray(cache.clientsCache)
        ? cache.clientsCache
        : [];
      const wsMatches = baseWs
        .map((w) => {
          const wid = String(w?.id || "").trim();
          if (!wid) return null;
          const label = resolveWorkspaceLabel(w);
          const drive = String(
            w?.driveFolderId || w?.driveFolderInput || "",
          ).trim();
          const hay = norm(`${label} ${drive}`);
          return hay.includes(t)
            ? { kind: "WORKSPACE", id: wid, label, raw: w }
            : null;
        })
        .filter(Boolean)
        .slice(0, 25);
      const clientMatches = baseClients
        .map((c) => {
          const cid = String(c?.id || "").trim();
          if (!cid) return null;
          const name = resolveClientName(c);
          const cpf = String(c?.cpf || c?.qualification?.physical?.cpf || "");
          const cnpj = String(c?.cnpj || c?.qualification?.legal?.cnpj || "");
          const email = String(
            c?.contact?.email || c?.contact?.emailPrimary || "",
          );
          const hay = norm(
            `${name} ${cpf} ${cnpj} ${email} ${c?.qualificationText || ""}`,
          );
          return hay.includes(t)
            ? {
                kind: "CLIENT",
                id: cid,
                label: name,
                raw: { ...c, __displayName: name },
              }
            : null;
        })
        .filter(Boolean)
        .slice(0, 20);
      search.setLinkResults([...wsMatches, ...clientMatches]);
    },
    [
      cache.clientsCache,
      cache.workspacesCache,
      loadWorkspacesCache,
      loadClientsCache,
      norm,
      resolveClientName,
      resolveWorkspaceLabel,
      search,
    ],
  );

  const pushNotice = useCallback(
    (msg) => {
      fb.setNotice(msg);
      if (noticeTimerRef.current) {
        clearTimeout(noticeTimerRef.current);
      }
      noticeTimerRef.current = setTimeout(() => {
        fb.setNotice("");
        noticeTimerRef.current = null;
      }, 3000);
    },
    [fb],
  );

  const updateSlotName = useCallback(
    (slotId, nextValue) => {
      const sid = String(slotId);
      setSlotNames((prev) => {
        if (prev?.[sid] === nextValue) return prev;
        return { ...prev, [sid]: nextValue };
      });
    },
    [setSlotNames],
  );

  const moveSlot = useCallback(
    (slotId, dir) => {
      const sid = String(slotId);
      setSlotOrder((prev) => {
        const idx = prev.indexOf(sid);
        if (idx < 0) return prev;
        const next = [...prev];
        const swap = idx + dir;
        if (swap < 0 || swap >= next.length) return prev;
        [next[idx], next[swap]] = [next[swap], next[idx]];
        return next;
      });
    },
    [setSlotOrder],
  );

  const handleAddSlot = useCallback(() => {
    if (isMirrorMode) return;
    const newId = `slot-extra-${Date.now()}`;
    setSlotOrder((prev) => [...(Array.isArray(prev) ? prev : []), newId]);
    setClientRequested((prev) => ({ ...(prev || {}), [newId]: true }));
  }, [isMirrorMode, setSlotOrder, setClientRequested]);

  const postBridgeState = useCallback(
    async (payload) => {
      const baseUrl = getBaseUrl();
      if (!baseUrl) return;
      // FIX P16 V3: Se parties foram fornecidas, construir payload completo
      // em vez de depender do useEffect com activeWorkspace (stale closure)
      const providedParties = payload?.parties;
      const finalPayload = providedParties
        ? {
            workspaceId: workspaceKey,
            state: {
              assignments: assignments || {},
              driveFiles: Array.isArray(drive.driveFiles)
                ? drive.driveFiles
                : [],
              checklist: Array.isArray(checklist.checklist)
                ? checklist.checklist
                : [],
              slotOrder: Array.isArray(slotOrder) ? slotOrder : [],
              slotNames:
                slotNames && typeof slotNames === "object" ? slotNames : {},
              parties: providedParties,
              pieceType: String(pieceType || "").trim(),
              numeroInicialAnexos,
              rotulosPorSlot: rotulosDosAnexos,
            },
          }
        : payload;
      const url = `${baseUrl}/bridge/state`;
      try {
        await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(finalPayload),
        });
      } catch {}
    },
    [
      workspaceKey,
      assignments,
      drive.driveFiles,
      checklist.checklist,
      slotOrder,
      slotNames,
      pieceType,
      numeroInicialAnexos,
      rotulosDosAnexos,
    ],
  );
  // postBridgeStateRef: mesmo padrão de syncWorkspaceNowRef/listDriveFolderRef
  // (ver mais abaixo) — postBridgeState já depende de assignments/driveFiles/
  // checklist/slotOrder/slotNames/pieceType, então sua identidade muda em
  // TODA edição. O efeito que chama postBridgeState (linha ~3625) também
  // listava assignments/driveFiles/checklist/slotOrder direto — ou seja,
  // cada edição disparava esse efeito duas vezes por caminhos diferentes
  // (uma vez pelos valores mudarem, outra pela função ter mudado de
  // identidade), dobrando o volume de POST /bridge/state. Usar a ref quebra
  // esse duplo-disparo sem mudar nenhum comportamento — o efeito chama a
  // versão mais recente sem precisar da função como dependência.
  const postBridgeStateRef = useRef(postBridgeState);
  useEffect(() => {
    postBridgeStateRef.current = postBridgeState;
  }, [postBridgeState]);

  const handleSaveMetadata = useCallback(async () => {
    if (!workspaceDocRef) return;
    const resolvedPid =
      meta.metaProcessMode === "npu" ? meta.metaNpu.trim() || null : null;
    const resolvedNote =
      meta.metaProcessMode === "alias" ? meta.metaAlias.trim() || null : null;
    try {
      await saveWorkspaceDoc(workspaceDocRef, {
        ancestry: {
          client_id: String(activeWorkspace?.clientId || "").trim() || null,
          primary_process_id: resolvedPid,
          pre_processual_note: resolvedNote,
          jurisdiction_branch:
            meta.metaJurisdiction.length > 0 ? meta.metaJurisdiction : null,
          depth_level: 1,
        },
        metadata: {
          liminar: meta.metaLiminar || "NAO",
          liminar_tipo:
            meta.metaLiminar === "SIM"
              ? meta.metaLiminarTipo.trim() || null
              : null,
          objeto: meta.metaObjeto.trim() || null,
          parte_contraria:
            meta.metaPartesContrarias.length > 0
              ? JSON.stringify(meta.metaPartesContrarias)
              : null,
          resumo: meta.metaResumo.trim() || null,
          urgencia: meta.metaUrgencia || "MEDIA",
          justica_gratuita: meta.metaGratuita || "NAO",
          justica_gratuita_justificativa:
            meta.metaGratuita === "SIM"
              ? meta.metaGratuitaJust.trim() || null
              : null,
        },
        updatedAt: serverTimestamp(),
      });
      pushNotice("Metadados do caderno atualizados.");
      meta.setMetaOpen(false);
    } catch (e) {
      pushNotice("Erro ao salvar metadados: " + String(e?.message || e));
    }
  }, [workspaceDocRef, meta, activeWorkspace?.clientId, pushNotice]);

  // Lógica compartilhada de rename de pasta no Drive (não-bloqueante).
  // FIX ESTRUTURA CADERNO > PEÇAS: recebe o folderId explicitamente — cada
  // peça tem sua própria pasta agora, então não dá mais para "adivinhar"
  // qual pasta renomear a partir de estado local (pieceType/activeWorkspace).
  // O caller (CadernoCockpit.jsx) é quem sabe qual peça está sendo renomeada.
  //
  // FIX TRAVA POR PASTA (21/08/2026): a trava antiga (pieceTypeChangeInProgressRef,
  // um único boolean) foi desenhada para quando só existia UMA pasta por caderno —
  // um rename por vez fazia sentido. Agora que cada peça tem pasta própria, renomear
  // a peça 2 rápido depois da peça 1 fazia a peça 2 ser IGNORADA silenciosamente
  // porque a trava da peça 1 ainda estava "ligada" (fetch anterior ainda em voo).
  // Confirmado em produção: 2 de 3 peças da Vanessa ficaram com a pasta presa em
  // "Nova Peça" porque o rename real nunca chegou a ser disparado. Agora a trava é
  // por folderId (Set), então renomear pastas DIFERENTES nunca bloqueia uma à outra.
  const renamePieceFolderOnDrive = useCallback(
    (folderId, newName) => {
      const draft = String(newName || "").trim();
      const cleanFolderId = String(folderId || "").trim();
      if (!draft || !cleanFolderId) return;
      if (pieceRenameInFlightRef.current.has(cleanFolderId)) return;
      pieceRenameInFlightRef.current.add(cleanFolderId);
      const baseUrl = getBaseUrl();
      if (baseUrl) {
        fetchWithWarmup(`${baseUrl}/drive/rename-folder`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            folder_id: cleanFolderId,
            desired_name: draft,
          }),
        })
          .then((resp) => {
            if (resp && resp.ok)
              pushNotice(`Pasta renomeada para "${draft}" no Drive`);
            else pushNotice("Falha ao renomear pasta no Drive (não crítico)");
          })
          .catch(() => {
            pushNotice("Falha ao renomear pasta no Drive (não crítico)");
          })
          .finally(() => {
            pieceRenameInFlightRef.current.delete(cleanFolderId);
          });
      } else {
        pieceRenameInFlightRef.current.delete(cleanFolderId);
      }
    },
    [pushNotice],
  );

  // Apenas atualiza o que é exibido (título + checklist) para a peça
  // selecionada — NÃO mexe em nada no Drive. Trocar de peça na barra
  // lateral é navegação, não deve renomear pasta nenhuma.
  const applyPieceTypeLabel = useCallback(
    (nextType) => {
      const draft = String(nextType || "").trim();
      if (!draft) return;
      setPieceType(draft);
      checklist.setChecklist(getChecklistDefault(draft));
    },
    [setPieceType, checklist.setChecklist],
  );

  const autoFill = useCallback(() => {
    const available = Array.isArray(drive.driveFiles) ? drive.driveFiles : [];
    if (!available.length) return;
    // Extrai o número do ANEXO já presente no nome do arquivo (ex.:
    // "ANEXO_20_CONVERSAS_GMAIL.pdf" -> 20). Cobre "ANEXO_20_", "ANEXO 20 -",
    // "anexo-20_" etc.
    // Função única (aceita 2 e 3 dígitos, com zeros à esquerda).
    const extractAnexoNumber = (name) => numeroNoNome(name);
    setAssignments((prev) => {
      const next = { ...(prev || {}) };
      const used = new Set(
        Object.values(next)
          .map((v) => String(v?.id || ""))
          .filter(Boolean),
      );
      let freeFiles = available.filter((f) => !used.has(String(f.id)));
      if (!freeFiles.length) return prev;
      const order = Array.isArray(slotOrder) ? slotOrder : [];
      const emptySlots = order.filter((sid) => !next[sid]);
      if (!emptySlots.length) return prev;
      const assign = (sid, f) => {
        next[sid] = {
          id: String(f.id),
          name: String(f.name || ""),
          url: String(f.webViewLink || ""),
        };
        freeFiles = freeFiles.filter((x) => String(x.id) !== String(f.id));
      };
      // Passo 1: casa pelo número do ANEXO já presente no nome do arquivo —
      // recupera vínculos de arquivos já renomeados/organizados antes, sem
      // arriscar colocar o arquivo no slot errado.
      emptySlots.forEach((sid) => {
        if (next[sid]) return;
        const idx = order.indexOf(sid);
        if (idx < 0) return;
        // Com "começar em" ≠ 1, o slot da posição idx tem o número idx + início (U4: sem isto,
        // arquivo já renomeado "ANEXO 30 …" cairia no slot errado).
        const wantedNumber = numeroDoAnexo(idx, numeroInicialAnexos);
        const f = freeFiles.find(
          (file) => extractAnexoNumber(file.name) === wantedNumber,
        );
        if (f) assign(sid, f);
      });
      // Passo 2: para slots ainda vazios, preenche posicionalmente com os
      // arquivos livres restantes (fluxo original — intake de arquivo novo
      // ainda sem nome de ANEXO).
      emptySlots.forEach((sid) => {
        if (next[sid]) return;
        const f = freeFiles[0];
        if (!f) return;
        assign(sid, f);
      });
      return next;
    });
  }, [drive.driveFiles, slotOrder, numeroInicialAnexos, setAssignments]);

  const saveChecklistAsDefault = useCallback(async () => {
    try {
      const t = String(pieceType || "").trim();
      if (!t) return;
      const chk = Array.isArray(checklist.checklist) ? checklist.checklist : [];
      saveJson(`rda_chk_template_${t}`, chk);
      const aid = String(FIREBASE_CONFIG?.projectId || appId || "").trim();
      if (aid) {
        try {
          const emailVal = String(email || "").trim() || null;
          await saveTemplate(aid, t, chk, slotNames, emailVal);
          pushNotice("Padrão salvo no escritório ☁");
        } catch (fbErr) {
          console.warn(
            "[TEMPLATE] Falha ao salvar template no Firestore:",
            fbErr,
          );
          pushNotice("Padrão salvo localmente (falha na nuvem).");
        }
      } else {
        pushNotice("Padrão salvo localmente.");
      }
    } catch (err) {
      console.error("[TEMPLATE] Erro inesperado:", err);
      pushNotice("Erro ao salvar padrão.");
    }
  }, [pieceType, checklist.checklist, slotNames, pushNotice]);

  const handleNukeWorkspace = useCallback(async () => {
    // MC60 (03/09/2026): confirmação trocada de palavra genérica
    // ("DELETAR") pra nome do cliente de verdade — checagem aqui tem que
    // bater com o mesmo texto exigido na tela (FileHubArea.jsx, modal
    // nukeOpen), senão o botão fica habilitado na tela mas essa função
    // continua recusando silenciosamente.
    const nomeCliente = String(activeWorkspace?.clientName || "").trim();
    const confirmAlvo = (nomeCliente || "DELETAR").toUpperCase();
    if (!workspaceDocRef || nukeText.trim().toUpperCase() !== confirmAlvo) return;
    modals.setNukeBusy(true);
    try {
      const baseUrl = getBaseUrl();
      if (baseUrl) {
        const token = await getToken().catch(() => null);
        // FIX ESTRUTURA CADERNO > PEÇAS: cada peça agora tem sua própria
        // pasta — apagar o caderno precisa varrer a pasta do caderno em si
        // e a de CADA peça, não só os campos (legados) do nível do workspace.
        const piecesList = Array.isArray(activeWorkspace?.pieces)
          ? activeWorkspace.pieces
          : [];
        const folderIds = [
          ...new Set(
            [
              String(activeWorkspace?.cadernoFolderId || ""),
              String(activeWorkspace?.processPieceFolderId || ""),
              String(activeWorkspace?.attachmentsFolderId || ""),
              String(activeWorkspace?.driveFolderId || ""),
              ...piecesList.map((p) => String(p?.processPieceFolderId || "")),
              ...piecesList.map((p) => String(p?.attachmentsFolderId || "")),
            ].filter((id) => id && id !== "undefined" && id !== "null"),
          ),
        ];
        if (folderIds.length > 0) {
          const driveResults = await Promise.allSettled(
            folderIds.map((folderId) =>
              fetch(`${baseUrl}/drive/delete-folder`, {
                method: "DELETE",
                headers: {
                  "Content-Type": "application/json",
                  ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify({ folderId }),
              }).then(async (res) => {
                const data = await res.json().catch(() => ({}));
                if (!res.ok || !data?.ok)
                  throw new Error(
                    String(
                      data?.detail || data?.error || "Drive delete failed",
                    ),
                  );
                return data;
              }),
            ),
          );
          const failures = driveResults.filter((r) => r.status === "rejected");
          if (failures.length > 0) {
            const reasons = failures
              .map((r) =>
                String(r.reason?.message || r.reason || "Erro desconhecido"),
              )
              .join("; ");
            fb.setError(
              `⚠️ Falha ao apagar ${failures.length} pasta(s) no Drive. Registro mantido no banco para evitar zumbis. Verifique permissões (papel Manager). Detalhe: ${reasons}`,
            );
            return;
          }
        }
      }
      await deleteWorkspace(workspaceDocRef);
      modals.setNukeOpen(false);
      modals.setNukeText("");
      if (typeof onBack === "function") onBack();
    } catch (e) {
      fb.setError(String(e?.message || e));
    } finally {
      modals.setNukeBusy(false);
    }
  }, [workspaceDocRef, nukeText, activeWorkspace, onBack, fb, modals]);

  const handleCiteInWord = useCallback(
    // Espelho (24/09/2026): `rotulo` é o texto PRONTO ("ANEXO 07"/"ANEXO 007") calculado pela função
    // única no Portal, pela posição real do slot (numeroInicialAnexos + largura vigente) — nunca mais
    // pela posição do arquivo numa lista compactada de preenchidos. `orderedIds` fica só como índice
    // de fallback para insertAttachmentCitation quando `rotulo` não vier.
    async (baseLabel, fileId, fileUrl, orderedIds, rotulo) => {
      const cleanLabel = String(baseLabel || "").trim();
      const finalName =
        typeof sanitizeEvidenceName === "function"
          ? sanitizeEvidenceName(cleanLabel)
          : String(cleanLabel || "").toUpperCase();
      const id = String(fileId || "").trim();
      const url = String(fileUrl || "").trim();
      const ids = Array.isArray(orderedIds) ? orderedIds.map(String) : [];
      const idx = id ? ids.indexOf(String(id)) : -1;
      const index = idx >= 0 ? idx : 0;
      if (window?.Word && id) {
        await insertAttachmentCitation(
          { id, description: finalName, url, rotulo },
          index,
        );
        return;
      }
      if (navigator?.clipboard?.writeText) {
        navigator.clipboard.writeText(`(${finalName})`);
      }
    },
    [],
  );

  const handleResetSlot = useCallback(
    async (slotId) => {
      if (isMirrorMode) return;
      const sid = String(slotId);
      const lockTs = Number(uploadLocks?.[sid] || 0);
      if (lockTs > 0 && Date.now() - lockTs < 300000) {
        pushNotice(
          "⚠️ O cliente está enviando arquivos neste slot agora. Aguarde o envio terminar antes de resetar.",
        );
        return;
      }
      const mainFileId = String(assignments?.[sid]?.id || "").trim();
      if (!mainFileId) {
        pushNotice("Slot não possui arquivo vinculado.");
        return;
      }
      let multiFiles = Array.isArray(assignments?.[sid]?.multiFiles)
        ? assignments[sid].multiFiles
        : [];
      if (multiFiles.length === 0 && workspaceDocRef) {
        try {
          const { data: remoteData } = await fetchWorkspaceDoc(workspaceDocRef);
          const remoteAssign = remoteData?.organizerState?.assignments?.[sid];
          if (Array.isArray(remoteAssign?.multiFiles))
            multiFiles = remoteAssign.multiFiles;
        } catch {}
      }
      const allFileIds = [
        ...new Set(
          [
            ...multiFiles
              .map((mf) => String(mf?.id || "").trim())
              .filter(Boolean),
            mainFileId,
          ].filter(Boolean),
        ),
      ];
      const fileName = String(assignments?.[sid]?.name || "arquivo");
      const ok = window.confirm(
        `Apagar ${allFileIds.length} arquivo(s) do Drive e reabrir este slot para o cliente?`,
      );
      if (!ok) return;
      const baseUrl = getBaseUrl();
      const token = await getToken().catch(() => null);
      for (const fid of allFileIds) {
        try {
          if (baseUrl) {
            const resp = await fetchWithWarmup(`${baseUrl}/drive/delete`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
              },
              body: JSON.stringify({ fileId: fid }),
            });
            if (!resp.ok)
              console.warn(
                `Reset slot: falha ao apagar ${fid} do Drive (HTTP ${resp.status})`,
              );
          }
        } catch (err) {
          console.warn("Reset slot: erro ao apagar arquivo do Drive:", err);
        }
      }
      setAssignments((prev) => {
        const next = { ...(prev || {}) };
        delete next[sid];
        return next;
      });
      drive.setDriveFiles((prev) =>
        (prev || []).filter((f) => !allFileIds.includes(String(f?.id || ""))),
      );
      pushNotice(
        `Slot resetado: ${allFileIds.length} arquivo(s) apagado(s) do Drive e slot reaberto.`,
      );
      if (workspaceDocRef) {
        try {
          await patchWorkspaceDoc(workspaceDocRef, {
            [`organizerState.assignments.${sid}`]: deleteField(),
            [`organizerStates.${effectivePieceKey}.assignments.${sid}`]:
              deleteField(),
            organizerStateUpdatedAtMs: Date.now(),
            updatedAt: serverTimestamp(),
          });
        } catch (err) {
          console.error("Falha ao limpar slot no Firestore:", err);
        }
      }
    },
    [
      isMirrorMode,
      assignments,
      setAssignments,
      drive.setDriveFiles,
      pushNotice,
      workspaceDocRef,
      uploadLocks,
      effectivePieceKey,
    ],
  );

  const handleRemoveSlot = useCallback(
    async (slotId) => {
      if (isMirrorMode) return;
      const sid = String(slotId);
      const slotLabel = String(slotNames?.[sid] || sid);
      const mainFileId = String(assignments?.[sid]?.id || "").trim();
      let multiFiles = Array.isArray(assignments?.[sid]?.multiFiles)
        ? assignments[sid].multiFiles
        : [];
      if (multiFiles.length === 0 && workspaceDocRef) {
        try {
          const { data: remoteData } = await fetchWorkspaceDoc(workspaceDocRef);
          const remoteAssign = remoteData?.organizerState?.assignments?.[sid];
          if (Array.isArray(remoteAssign?.multiFiles))
            multiFiles = remoteAssign.multiFiles;
        } catch {}
      }
      const allFileIds = [
        ...new Set(
          [
            ...multiFiles
              .map((mf) => String(mf?.id || "").trim())
              .filter(Boolean),
            mainFileId,
          ].filter(Boolean),
        ),
      ];
      const hasFiles = allFileIds.length > 0;
      const ok = window.confirm(
        `Remover o slot "${slotLabel}"${hasFiles ? ` e apagar ${allFileIds.length} arquivo(s) do Drive` : ""}? Esta ação não pode ser desfeita.`,
      );
      if (!ok) return;
      if (hasFiles) {
        const baseUrl = getBaseUrl();
        for (const fid of allFileIds) {
          try {
            if (baseUrl) {
              await fetchWithWarmup(`${baseUrl}/drive/delete`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ fileId: fid }),
              });
            }
          } catch {}
        }
        drive.setDriveFiles((prev) =>
          (prev || []).filter((f) => !allFileIds.includes(String(f?.id || ""))),
        );
      }
      setSlotOrder((prev) =>
        (Array.isArray(prev) ? prev : []).filter((s) => s !== sid),
      );
      setAssignments((prev) => {
        const next = { ...(prev || {}) };
        delete next[sid];
        return next;
      });
      setSlotNames((prev) => {
        const next = { ...(prev || {}) };
        delete next[sid];
        return next;
      });
      setClientRequested((prev) => {
        const next = { ...(prev || {}) };
        delete next[sid];
        return next;
      });
      if (workspaceDocRef) {
        try {
          await patchWorkspaceDoc(workspaceDocRef, {
            "organizerState.slotOrder": arrayRemove(sid),
            [`organizerState.slotNames.${sid}`]: deleteField(),
            [`organizerState.clientRequested.${sid}`]: deleteField(),
            [`organizerState.assignments.${sid}`]: deleteField(),
            [`organizerStates.${effectivePieceKey}.slotOrder`]:
              arrayRemove(sid),
            [`organizerStates.${effectivePieceKey}.slotNames.${sid}`]:
              deleteField(),
            [`organizerStates.${effectivePieceKey}.clientRequested.${sid}`]:
              deleteField(),
            [`organizerStates.${effectivePieceKey}.assignments.${sid}`]:
              deleteField(),
            organizerStateUpdatedAtMs: Date.now(),
            updatedAt: serverTimestamp(),
          });
        } catch (err) {
          console.error("Falha ao remover slot do Firestore:", err);
        }
      }
      pushNotice(`Slot "${slotLabel}" removido.`);
    },
    [
      isMirrorMode,
      slotNames,
      assignments,
      drive.setDriveFiles,
      setSlotOrder,
      setAssignments,
      setSlotNames,
      setClientRequested,
      workspaceDocRef,
      pushNotice,
      effectivePieceKey,
    ],
  );

  const assignToSlot = useCallback(
    (slotId, fileId) => {
      const targetSlotId = String(slotId);
      const fid = String(fileId || "").trim();
      if (!fid) {
        setAssignments((prev) => {
          const next = { ...(prev || {}) };
          try {
            delete next[targetSlotId];
          } catch {}
          return next;
        });
        return;
      }
      const file = (drive.driveFiles || []).find(
        (f) => String(f?.id || "") === fid,
      );
      setAssignments((prev) => {
        const next = { ...(prev || {}) };
        const targetExistingId = String(next?.[targetSlotId]?.id || "").trim();
        let existingSlotId = null;
        for (const [sid, v] of Object.entries(next)) {
          if (String(v?.id || "").trim() === fid) {
            existingSlotId = String(sid);
            break;
          }
        }
        const needMove = Boolean(
          existingSlotId && existingSlotId !== targetSlotId,
        );
        const needOverwrite = Boolean(
          targetExistingId && targetExistingId !== fid,
        );
        if (hostMode === "word" && (needMove || needOverwrite)) {
          try {
            setPendingAssign({
              targetSlotId,
              fid,
              file: file || null,
              existingSlotId: needMove ? existingSlotId : null,
              targetExistingId: needOverwrite ? targetExistingId : null,
            });
          } catch {}
          return prev;
        }
        if (existingSlotId && existingSlotId !== targetSlotId) {
          const ok = window.confirm(
            `Este PDF já está vinculado ao slot ${existingSlotId}.\n\nDeseja mover o vínculo para ${targetSlotId}?`,
          );
          if (!ok) return prev;
          try {
            delete next[existingSlotId];
          } catch {}
        }
        if (targetExistingId && targetExistingId !== fid) {
          const ok = window.confirm(
            `O slot ${targetSlotId} já possui um PDF vinculado.\n\nDeseja substituir?`,
          );
          if (!ok) return prev;
        }
        next[targetSlotId] = {
          id: fid,
          name: String(file?.name || ""),
          url: String(file?.webViewLink || ""),
        };
        try {
          if (hostMode !== "word") {
            const wsId = String(workspaceKey || "").trim();
            const driveFileId = String(fid || "").trim();
            const fileName = String(file?.name || "").trim();
            const fromWorkspace = Array.isArray(workspacePartyRefs)
              ? workspacePartyRefs
                  .map((p) => String(p?.clientId || "").trim())
                  .filter(Boolean)
              : [];
            const primaryClientId = String(
              activeWorkspace?.clientId || "",
            ).trim();
            const parties = Array.from(
              new Set([primaryClientId, ...fromWorkspace].filter(Boolean)),
            );
            if (wsId && driveFileId && parties.length) {
              const linkId = `${wsId}_${driveFileId}`;
              fetchGedLink(linkId)
                .then((data) => {
                  const existed = Boolean(Object.keys(data).length > 0);
                  return saveGedLink(linkId, {
                    linkId,
                    driveFileId,
                    fileName,
                    workspaceId: wsId,
                    partiesRefs: parties,
                  }).then(() => ({ existed }));
                })
                .then(({ existed }) => {
                  if (existed) return;
                  try {
                    parties.forEach((clientId) => {
                      const cid = String(clientId || "").trim();
                      if (!cid) return;
                      addClientNote(appId, cid, {
                        category: "SYSTEM",
                        type: "DOC_UPLOADED",
                        payload: {
                          fileName: String(fileName || "").trim() || "Sem nome",
                          text: "Documento vinculado ao acervo",
                          workspaceId: wsId,
                          workspaceLabel:
                            resolveWorkspaceLabel(activeWorkspace),
                        },
                        actor: { name: "Sistema", isBot: true },
                      }).catch((e) => {
                        try {
                          console.warn(
                            "[Prontuario] Falha ao gravar SYSTEM_DOC note:",
                            e,
                          );
                        } catch {}
                      });
                    });
                  } catch {}
                  try {
                    const wsLabel =
                      resolveWorkspaceLabel(activeWorkspace) || wsId;
                    const npuRaw = String(
                      activeWorkspace?.ancestry?.primary_process_id || "",
                    ).trim();
                    const npuPart = npuRaw ? ` — Processo ${npuRaw}` : "";
                    createOfficeNotification(
                      `Novo documento enviado`,
                      `${String(fileName || "Arquivo").trim()} foi vinculado ao caderno "${wsLabel}"${npuPart}.`,
                      "DOCUMENT_UPLOADED",
                      null,
                    );
                  } catch {}
                })
                .catch((e) => {
                  try {
                    console.warn("[GED] Falha ao gravar rda_ged_links:", e);
                  } catch {}
                });
            }
          }
        } catch {}
        return next;
      });
    },
    [
      drive.driveFiles,
      setAssignments,
      hostMode,
      workspaceKey,
      workspacePartyRefs,
      activeWorkspace,
      resolveWorkspaceLabel,
      setPendingAssign,
    ],
  );

  const applyPendingAssign = useCallback(
    ({ move, overwrite }) => {
      const p = pendingAssign;
      if (!p?.targetSlotId || !p?.fid) {
        setPendingAssign(null);
        return;
      }
      setAssignments((prev) => {
        const next = { ...(prev || {}) };
        if (move && p.existingSlotId && p.existingSlotId !== p.targetSlotId) {
          try {
            delete next[p.existingSlotId];
          } catch {}
        }
        if (!overwrite && p.targetExistingId && p.targetExistingId !== p.fid) {
          return prev;
        }
        next[p.targetSlotId] = {
          id: String(p.fid),
          name: String(p.file?.name || ""),
          url: String(p.file?.webViewLink || ""),
        };
        return next;
      });
      setPendingAssign(null);
    },
    [pendingAssign, setAssignments],
  );

  const handleDragEnd = useCallback(
    (event) => {
      if (isMirrorMode) return;
      const activeId = String(event?.active?.id || "");
      const overId = String(event?.over?.id || "");
      if (!activeId || !overId || activeId === overId) return;
      const currentOrder = Array.isArray(slotOrder) ? slotOrder : [];
      if (currentOrder.includes(activeId) && currentOrder.includes(overId)) {
        setSlotOrder((prev) => {
          const arr = Array.isArray(prev) ? prev.map(String) : [];
          const oldIndex = arr.indexOf(activeId);
          const newIndex = arr.indexOf(overId);
          if (oldIndex < 0 || newIndex < 0) return prev;
          return arrayMove(arr, oldIndex, newIndex);
        });
        return;
      }
      if (overId.startsWith("drop-slot-")) {
        const slotId = overId.replace("drop-", "");
        if (!slotId.startsWith("slot-")) return;
        if (activeId.startsWith("slot-")) return;
        assignToSlot(slotId, activeId);
      }
    },
    [assignToSlot, drive.driveFiles, setSlotOrder, slotOrder],
  );

  const uploadAndAssign = useCallback(
    async (slotId, file, desiredName) => {
      if (!file) return;
      if (!drive.driveFolderId) {
        pushNotice("Conecte uma pasta do Drive antes de fazer upload.");
        return;
      }
      const safeName = String(
        desiredName || file.name || "documento.pdf",
      ).trim();
      pushNotice(`Enviando "${safeName}"...`);
      try {
        const token = await getToken(true).catch(() => null);
        if (!token) {
          pushNotice("Sessão expirada. Faça login novamente.");
          return;
        }
        const baseUrl = getBaseUrl();
        if (!baseUrl) {
          pushNotice("Backend indisponível.");
          return;
        }
        const form = new FormData();
        form.append("file", file, safeName);
        form.append("parentId", drive.driveFolderId);
        form.append("fileName", safeName);
        form.append("makePublic", "false");
        const resp = await fetchWithWarmup(`${baseUrl}/drive/upload-file`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: form,
        });
        const result = await resp.json().catch(() => ({}));
        if (!resp.ok)
          throw new Error(
            String(result?.detail || result?.error || "Falha no upload."),
          );
        const fileId = String(result?.fileId || result?.id || "").trim();
        const fileUrl = String(
          result?.webViewLink || result?.webContentLink || "",
        ).trim();
        if (!fileId) throw new Error("Drive não retornou fileId.");
        drive.setDriveFiles((prev) => [
          ...(prev || []),
          {
            id: fileId,
            name: safeName,
            webViewLink: fileUrl,
            mimeType: file.type || "application/pdf",
          },
        ]);
        assignToSlot(slotId, fileId);
        await (listDriveFolderRef
          .current?.(drive.driveFolderId, { silent: true })
          .catch(() => {}) ?? Promise.resolve());
        pushNotice(`"${safeName}" enviado e vinculado com sucesso!`);
      } catch (e) {
        pushNotice(`Falha no upload: ${String(e?.message || e).slice(0, 100)}`);
      }
    },
    [drive.driveFolderId, assignToSlot, pushNotice],
  );

  const renameSingleFile = useCallback(
    async (fileId, desiredName, { skipRelist = false } = {}) => {
      if (isMirrorMode) return;
      const cleanFileId = String(fileId || "").trim();
      if (!cleanFileId) return;
      const rawNextName = String(desiredName || "").trim();
      if (!rawNextName) return;
      const incomingExt = getExtFromName(rawNextName);
      const nextName = ensurePdfExt(rawNextName, incomingExt);
      if (!nextName) return;
      try {
        const baseUrl = getBaseUrl();
        if (!baseUrl) {
          throw new Error(
            "Falha de Conexão: Não foi possível comunicar com os servidores RDA na Nuvem. O documento será retido na fila.",
          );
        }
        const resp = await fetchWithWarmup(`${baseUrl}/drive/rename`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            file_id: cleanFileId,
            desired_name: nextName,
          }),
        }).catch(() => null);
        if (!resp)
          throw new Error(
            "Falha de Conexão: Não foi possível comunicar com os servidores RDA na Nuvem.",
          );
        const ct = String(resp.headers.get("content-type") || "").toLowerCase();
        if (!ct.includes("application/json")) {
          const txt = await resp.text().catch(() => "");
          const head = String(txt || "").slice(0, 200);
          throw new Error(`API retornou HTML em vez de JSON. Início: ${head}`);
        }
        const json = await resp.json().catch(() => null);
        if (!resp.ok || !json?.ok) {
          const errDetail =
            json?.detail || json?.error || "Falha ao renomear arquivo no Drive";
          console.error(
            `[RENAME] FALHA fileId=${cleanFileId} → "${nextName}" | status=${resp.status} | detalhe:`,
            errDetail,
          );
          if (
            resp.status === 409 ||
            String(errDetail).includes("CONTENT_RESTRICTION")
          ) {
            throw new Error(
              "🔒 Arquivo bloqueado no Drive — remova o cadeado manualmente (botão direito → Informações → Restrições de conteúdo) e tente novamente.",
            );
          }
          throw new Error(errDetail);
        }
        drive.setDriveFiles((prev) =>
          prev.map((f) => {
            if (String(f.id) === cleanFileId)
              return { ...f, name: String(json.name || nextName) };
            return f;
          }),
        );
        // FIX NOME CONGELADO EM "DOCUMENTOS DA PEÇA" (16/09/2026): o painel
        // "Documentos da Peça" mostra assignments[slotId].name — uma cópia
        // separada do nome do arquivo, própria de cada slot, DIFERENTE de
        // drive.driveFiles (que já era atualizado acima). Renomear o arquivo
        // (ex.: via "Salvar & Organizar") trocava o nome real no Drive e em
        // driveFiles, mas nunca em assignments — o slot continuava mostrando
        // o nome de antes do rename pra sempre, até o usuário desvincular e
        // vincular o mesmo arquivo de novo. Vínculo (fileId) sempre estava
        // certo; só o texto exibido ficava congelado. Confirmado com dado
        // real: caso [PET_A], peça "Ressarcimento" (16/09/2026).
        setAssignments((prev) => {
          let changed = false;
          const next = { ...(prev || {}) };
          for (const [slotId, a] of Object.entries(next)) {
            if (a && String(a.id) === cleanFileId) {
              next[slotId] = { ...a, name: String(json.name || nextName) };
              changed = true;
            }
          }
          return changed ? next : prev;
        });
        if (drive.driveFolderId && !skipRelist) {
          await (listDriveFolderRef.current?.(drive.driveFolderId, {
            silent: true,
          }) ?? Promise.resolve());
        }
        pushNotice("Renomeado no Drive");
        return true;
      } catch (e) {
        pushNotice(`Falha ao renomear no Drive: ${String(e?.message || e)}`);
        return false;
      }
    },
    [drive.driveFolderId, pushNotice, setAssignments],
  );

  const applyRenameOnDrive = useCallback(async () => {
    // FIX LENTIDÃO "Salvar & Organizar" (21/08/2026): antes, cada arquivo
    // renomeado disparava uma releitura COMPLETA da pasta do Drive (dentro de
    // renameSingleFile), em série — para N anexos isso são N renomeações +
    // N releituras de pasta, uma de cada vez. Um caderno com 5 anexos fazia
    // 10 idas e voltas sequenciais à rede antes do botão liberar. Agora as
    // renomeações rodam em paralelo (Promise.all) e a pasta só é relida UMA
    // vez, no final.
    if (isMirrorMode) return;
    if (isRenaming) return;
    setIsRenaming(true);
    try {
      const ordered = Array.isArray(slotOrder) ? slotOrder : [];
      const toRename = [];
      for (let pos = 0; pos < ordered.length; pos += 1) {
        const sid = ordered[pos];
        const assignedId = assignments?.[sid]?.id;
        if (!assignedId) continue;
        const idx = Number(String(sid).replace("slot-", ""));
        const slotLabel = checklist.checklist?.[idx];
        const nameValue = String(slotNames?.[sid] ?? slotLabel ?? "");
        const base = nameValue.trim() ? nameValue.trim() : slotLabel;
        // MC06c (24/09/2026): slot SEM nome (ex.: slot extra sem slotName, `checklist[NaN]`) deixava
        // `base` undefined e o arquivo REAL era renomeado para "ANEXO_30_UNDEFINED.pdf" (visto ao vivo
        // no caso Allyne). Sem nome para montar, não renomeia: mantém o nome que o arquivo já tem.
        if (!String(base ?? "").trim()) continue;
        const origExt = getExtFromName(assignments?.[sid]?.name || "");
        const desired = ensurePdfExt(
          // Função única: "ANEXO 07" / "ANEXO 007" (largura vigente + "começar em").
          toSafeBase(`${rotuloDoSlot(pos, ordered.length, numeroInicialAnexos)} ${base}`),
          origExt,
        );
        // MC06b (24/09/2026, U26): só renomeia o que MUDOU. Antes, todo Salvar & Organizar mandava
        // uma chamada por arquivo ao backend do Render (que sempre limpa restrição, atualiza e
        // retrava), mesmo com o nome já certo. Compara com o nome atual do arquivo no Drive
        // (driveFiles) e, se ele não estiver listado, com a cópia do vínculo (assignments).
        const arquivoNoDrive = (drive.driveFiles || []).find(
          (f) => String(f?.id) === String(assignedId),
        );
        const nomeAtual = String(arquivoNoDrive?.name ?? assignments?.[sid]?.name ?? "");
        if (nomeAtual === desired) continue;
        toRename.push({ assignedId, desired });
      }
      // ITEM C (26/08/2026): "done" incrementa a cada arquivo que termina
      // (sucesso ou falha), pra tela de progresso mostrar "X de Y" em tempo
      // real, não só no fim.
      setRenameProgress({ done: 0, total: toRename.length });
      // MC06b (U26): no máximo 5 renomeações ao mesmo tempo (antes: todas de uma vez,
      // Promise.all). Com ~100 anexos e a virada de 2 para 3 dígitos, ~100 chamadas
      // simultâneas ao Render podem estourar tempo/limite e deixar a numeração MISTA.
      const resultados = await executarComLimite(
        toRename,
        LIMITE_RENOMEACOES_SIMULTANEAS,
        ({ assignedId, desired }) =>
          renameSingleFile(assignedId, desired, { skipRelist: true }).finally(
            () => setRenameProgress((p) => ({ ...p, done: p.done + 1 })),
          ),
      );
      if (toRename.length > 0 && drive.driveFolderId) {
        await (listDriveFolderRef.current?.(drive.driveFolderId, {
          silent: true,
        }) ?? Promise.resolve());
      }
      const total = toRename.length;
      const deramCerto = resultados.filter((r) => r === true).length;
      const falharam = total - deramCerto;
      pushNotice(
        total === 0
          ? "Renomeação concluída: nenhum arquivo precisava mudar de nome."
          : falharam > 0
            ? `Renomeação: ${deramCerto} de ${total} arquivos. ${falharam} falharam — clique em Salvar & Organizar de novo para repetir só esses.`
            : `Renomeação concluída: ${deramCerto} de ${total} arquivos.`,
      );
    } finally {
      setIsRenaming(false);
      setRenameProgress({ done: 0, total: 0 });
    }
  }, [
    isRenaming,
    slotOrder,
    assignments,
    checklist.checklist,
    slotNames,
    numeroInicialAnexos,
    renameSingleFile,
    pushNotice,
    drive.driveFolderId,
    drive.driveFiles,
  ]);

  const handleMasterSyncAll = useCallback(async () => {
    if (isMirrorMode) return;
    await (syncWorkspaceNowRef.current?.() ?? Promise.resolve());
    await applyRenameOnDrive();
  }, [isMirrorMode, applyRenameOnDrive]);

  // Ref para evitar loop de rename de pasta no Drive (usado só pelo
  // commitPieceType legado, abaixo — renamePieceFolderOnDrive usa
  // pieceRenameInFlightRef, declarado perto do topo do hook).
  const pieceTypeChangeInProgressRef = useRef(false);

  const commitPieceType = useCallback(
    (nextType) => {
      const draft = String(nextType || "").trim();
      if (!workspaceDocRef || !draft) return;
      const prevType = pieceType || "";
      const _v3docs = resolveCadernoDocumentosV3(draft);
      const _v2docs =
        _v3docs.length === 0 ? resolveCadernoDocumentos(draft) : [];
      const kit =
        (_v3docs.length > 0 ? _v3docs : _v2docs).length > 0
          ? (_v3docs.length > 0 ? _v3docs : _v2docs).map((d) => d.label)
          : CHECKLISTS[draft] || PIECE_KITS[draft] || KIT_DEFAULT;
      const freshSlotOrder = kit.map((_, i) => `slot-${i}`);
      const newSlotNames = {};
      const newClientReq = {};
      freshSlotOrder.forEach((sid, idx) => {
        newSlotNames[sid] = kit[idx];
        newClientReq[sid] = true;
      });
      const newOrgState = {
        assignments: {},
        slotNames: newSlotNames,
        slotOrder: freshSlotOrder,
        clientRequested: newClientReq,
        // Trocar o TIPO da peça não pode zerar o "começar em" dela.
        numeroInicialAnexos,
      };
      const nextLocalKey = `rda_autos_state_${drive.driveFolderId}_${effectivePieceKey}`;
      if (nextLocalKey) saveJson(nextLocalKey, newOrgState);
      setAssignments({});
      setSlotNames(newSlotNames);
      setSlotOrder(freshSlotOrder);
      setClientRequested(newClientReq);
      setPieceType(draft);
      checklist.setChecklist(getChecklistDefault(draft));

      // Prepara payload base do Firestore
      // FIX CAMPO-FANTASMA (26/08/2026): chave computada com ponto literal
      // (`organizerStates.${effectivePieceKey}`) dentro de um objeto passado
      // para setDoc(...,{merge:true}) NÃO faz merge aninhado — cria um campo
      // top-level literal com o ponto no nome, ao lado do map correto. Só
      // `.update()`/updateDoc() faz split nativo de string-com-ponto; para
      // setDoc(merge:true), o objeto precisa já vir aninhado de verdade.
      // Confirmado: firebase/firebase-tools#3770 (comentário oficial da
      // equipe Firebase, 23/11/2021) e agents/caderno_autos/
      // AGENT_INGESTAO_DADOS_PROCESSO.md §7 (mesmo bug, em Python).
      const firestorePayload = {
        pieceType: draft,
        processPieceFolderName: draft,
        organizerStates: { [effectivePieceKey]: newOrgState },
        organizerStateUpdatedAtMs: Date.now(),
        ancestry: {
          client_id: String(activeWorkspace?.clientId || "").trim() || null,
          primary_process_id:
            String(
              activeWorkspace?.ancestry?.primary_process_id || "",
            ).trim() || null,
          depth_level: 1,
        },
        updatedAt: serverTimestamp(),
      };

      // Se o pieceType mudou E existe processPieceFolderId, renomeia a pasta no Drive
      const processPieceFolderId = String(
        activeWorkspace?.processPieceFolderId || "",
      ).trim();
      if (
        prevType &&
        prevType !== draft &&
        processPieceFolderId &&
        !pieceTypeChangeInProgressRef.current
      ) {
        pieceTypeChangeInProgressRef.current = true;
        const baseUrl = getBaseUrl();
        if (baseUrl) {
          fetchWithWarmup(`${baseUrl}/drive/rename-folder`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              folder_id: processPieceFolderId,
              desired_name: draft,
            }),
          })
            .then((resp) => {
              if (resp && resp.ok)
                pushNotice(`Pasta renomeada para "${draft}" no Drive`);
              else pushNotice("Falha ao renomear pasta no Drive (não crítico)");
            })
            .catch(() => {
              pushNotice("Falha ao renomear pasta no Drive (não crítico)");
            })
            .finally(() => {
              pieceTypeChangeInProgressRef.current = false;
            });
        } else {
          pieceTypeChangeInProgressRef.current = false;
        }
      }

      saveWorkspaceDoc(workspaceDocRef, firestorePayload).catch(() => null);
      pushNotice(
        `Kit "${draft}" — ${kit.length} slot(s) · Lixo Fantasma expurgado`,
      );
    },
    [
      workspaceDocRef,
      drive.driveFolderId,
      effectivePieceKey,
      activeWorkspace,
      pieceType,
      numeroInicialAnexos,
      setAssignments,
      setSlotNames,
      setSlotOrder,
      setClientRequested,
      pushNotice,
    ],
  );

  const runPortalChaos = useCallback(async () => {
    if (chaosBusy) return;
    if (!workspaceKey) {
      fb.setNotice(
        "Selecione um workspace válido antes de executar o Chaos Test.",
      );
      return;
    }
    const baseUrl = getBaseUrl();
    if (!baseUrl) return;
    const url = `${baseUrl}/bridge/state`;
    fb.setError(null);
    fb.setNotice("Executando Chaos Test (Portal)...");
    setChaosBusy(true);
    const bigText = "X".repeat(25000);
    const bigArray = Array.from({ length: 250 }, (_, i) => ({
      id: `f_${i}`,
      name: `Arquivo ${i} ${bigText}`,
    }));
    const bigAssignments = Array.from({ length: 250 }, (_, i) => [
      `slot-${i}`,
      { id: `f_${i}` },
    ]);
    const payload = {
      workspaceId: workspaceKey || null,
      state: {
        assignments: Object.fromEntries(bigAssignments),
        driveFiles: bigArray,
        checklist: Array.from(
          { length: 250 },
          (_, i) => `Item ${i} ${bigText}`,
        ),
        slotOrder: Array.from({ length: 250 }, (_, i) => `slot-${i}`),
        parties: [],
      },
    };
    const tasks = [];
    for (let i = 0; i < 50; i += 1) {
      tasks.push(
        fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...payload, nonce: `${Date.now()}_${i}` }),
        }),
      );
    }
    let failures = 0;
    try {
      const results = await Promise.allSettled(tasks);
      for (const r of results) {
        if (r.status === "rejected") {
          failures += 1;
          continue;
        }
        if (!r.value?.ok) failures += 1;
      }
    } catch {
      failures = 50;
    } finally {
      setChaosBusy(false);
    }
    try {
      alert("Teste de Carga no Portal concluído.");
    } catch {}
    fb.setNotice(
      failures
        ? `Chaos Test concluído com ${failures} falhas.`
        : "Chaos Test concluído sem falhas.",
    );
  }, [chaosBusy, workspaceKey, fb, setChaosBusy]);

  const listDriveFolder = useCallback(
    async (folderId, { silent } = { silent: false }) => {
      if (isMirrorMode) {
        throw new Error(
          "No Word, o Organizador funciona como espelho (somente leitura). A conexão com o Drive deve ser feita no Navegador.",
        );
      }
      const baseUrl = getBaseUrl();
      if (!baseUrl) {
        throw new Error(
          "Falha de Conexão: Não foi possível comunicar com os servidores RDA na Nuvem. O documento será retido na fila.",
        );
      }
      const url = `${baseUrl}/drive/folder/${encodeURIComponent(folderId)}`;
      let controller;
      try {
        try {
          if (driveListAbortRef.current) {
            driveListAbortRef.current.abort();
          }
        } catch {}
        await warmUpBackend();
        const doFetchOnce = async () => {
          const started = fetchWithTimeout(url, {}, 15000);
          controller = started.controller;
          driveListAbortRef.current = controller;
          return await started.promise;
        };
        let resp = await doFetchOnce();
        if (resp && resp.status >= 500) {
          await new Promise((r) => setTimeout(r, 750));
          resp = await doFetchOnce();
        }
        if (!resp || !resp.ok) {
          const txt = resp ? await resp.text() : "";
          throw new Error(
            txt ||
              `Falha ao listar pasta do Drive (${resp ? resp.status : "rede"}).`,
          );
        }
        const ct = String(resp.headers.get("content-type") || "").toLowerCase();
        if (!ct.includes("application/json")) {
          const txt = await resp.text();
          const head = String(txt || "").slice(0, 200);
          throw new Error(
            `Resposta inesperada ao listar Drive (não é JSON). Início: ${head}`,
          );
        }
        const data = await resp.json();
        const items = data?.files || data?.items || data;
        if (
          items &&
          typeof items === "object" &&
          !Array.isArray(items) &&
          items.error
        ) {
          const msg = String(items.error || "Falha ao listar pasta do Drive");
          const sa = items.serviceAccountEmail || items.serviceAccount;
          const hint = sa ? `\n\nCompartilhe a pasta com: ${String(sa)}` : "";
          throw new Error(`${msg}${hint}`);
        }
        const normalized = Array.isArray(items) ? items : [];
        const mapped = normalized.map((it) => ({
          id: String(it?.id || ""),
          name: String(it?.name || ""),
          mimeType: String(it?.mimeType || ""),
          webViewLink: it?.webViewLink ? String(it.webViewLink) : null,
          modifiedTime: it?.modifiedTime ? String(it.modifiedTime) : null,
          size: it?.size ? Number(it.size) : null,
        }));
        const filesOnly = mapped.filter(
          (f) => !String(f.mimeType || "").includes("folder"),
        );
        let fullList = filesOnly;
        const discardFolder = mapped.find(
          (f) =>
            f.name === "_Descartados" &&
            String(f.mimeType || "").includes("folder"),
        );
        if (discardFolder) {
          try {
            const discardResp = await fetch(
              `${baseUrl}/drive/folder/${encodeURIComponent(discardFolder.id)}`,
            ).catch(() => null);
            if (discardResp?.ok) {
              const discardData = await discardResp.json().catch(() => null);
              const discardItems =
                discardData?.files || discardData?.items || discardData;
              if (Array.isArray(discardItems)) {
                const discardMapped = discardItems
                  .filter(
                    (it) =>
                      it?.id &&
                      it?.name &&
                      !String(it?.mimeType || "").includes("folder"),
                  )
                  .map((it) => ({
                    id: String(it.id),
                    name: String(it.name),
                    mimeType: String(it?.mimeType || ""),
                    webViewLink: it?.webViewLink
                      ? String(it.webViewLink)
                      : null,
                    modifiedTime: it?.modifiedTime
                      ? String(it.modifiedTime)
                      : null,
                    size: it?.size ? Number(it.size) : null,
                    _discarded: true,
                    _discardFolderId: discardFolder.id,
                  }));
                fullList = [...filesOnly, ...discardMapped];
              }
            }
          } catch {}
        }
        const sig = (list) =>
          list
            .map(
              (f) =>
                `${f.id}|${f.name}|${f.modifiedTime || ""}|${Number.isFinite(f.size) ? f.size : ""}|${f._discarded ? "1" : "0"}`,
            )
            .sort()
            .join(";;");
        if (!silent) {
          drive.setDriveFiles(fullList);
        } else {
          drive.setDriveFiles((prev) => {
            if (sig(prev) === sig(fullList)) return prev;
            return fullList;
          });
        }
        if (!silent) {
          try {
            localStorage.setItem(LS_ACTIVE_DRIVE_FOLDER, String(folderId));
            localStorage.setItem(LS_AUTOS_FOLDER, String(folderId));
          } catch {}
        }
      } catch (e) {
        const msg = String(e?.message || e);
        if (/aborted|abort/i.test(msg)) {
          throw new Error(
            "Falha de conexão com o Drive. Verifique sua conexão de internet e tente novamente.",
          );
        }
        throw new Error(
          msg ||
            "Falha de rede ao conectar no servidor. Tente novamente em alguns instantes.",
        );
      } finally {
        try {
          if (
            driveListAbortRef.current &&
            driveListAbortRef.current === controller
          ) {
            driveListAbortRef.current = null;
          }
        } catch {}
      }
    },
    [],
  );
  listDriveFolderRef.current = listDriveFolder;

  const connectToDriveFolder = useCallback(
    async (folderId, { resetState } = { resetState: true }) => {
      const cleanId = String(folderId || "").trim();
      if (!cleanId) return;
      fb.setError(null);
      fb.setNotice(null);
      setIsEdgeProcessing(false);
      drive.setIsLoading(true);
      drive.setDriveFolderId(cleanId);
      if (resetState) {
        const expected = checklist.checklist.map((_, idx) => `slot-${idx}`);
        drive.setDriveFiles([]);
        resetOrganizerStore();
        setSlotOrder(expected);
      }
      try {
        await listDriveFolder(cleanId, { silent: false });
        fb.setError(null);
      } catch (e) {
        fb.setError(String(e?.message || e));
      } finally {
        drive.setIsLoading(false);
        setIsEdgeProcessing(false);
      }
    },
    [listDriveFolder, checklist.checklist, resetOrganizerStore, setSlotOrder],
  );

  const refreshDriveFiles = useCallback(async () => {
    const folderId =
      drive.driveFolderId || extractDriveFolderId(driveFolderInput);
    if (!folderId) return;
    drive.setIsLoading(true);
    try {
      await listDriveFolder(folderId, { silent: false });
    } catch (e) {
      fb.setError(String(e?.message || e));
    } finally {
      drive.setIsLoading(false);
    }
  }, [drive.driveFolderId, driveFolderInput, listDriveFolder]);

  // FIX AUTO-REFRESH "ARQUIVOS NA NUVEM" (21/08/2026): a pasta conectada só
  // era listada uma vez, ao trocar de peça/caderno — se o usuário jogasse um
  // arquivo direto no Drive (ou pelo Google Drive do computador, que
  // sincroniza pra nuvem sozinho), o Portal nunca ficava sabendo, porque
  // nada relista a pasta sozinho. Agora, com uma pasta conectada, relista em
  // segundo plano a cada 20s (silencioso, sem popup de erro/aviso) — assim
  // um anexo que aparece na nuvem por fora do Portal aparece em "Arquivos na
  // Nuvem" sem precisar trocar de peça e voltar.
  useEffect(() => {
    if (isMirrorMode) return;
    const folderId = String(drive.driveFolderId || "").trim();
    if (!folderId) return;
    const interval = setInterval(() => {
      if (drive.isLoading) return;
      listDriveFolder(folderId, { silent: true }).catch(() => {});
    }, 20000);
    return () => clearInterval(interval);
  }, [isMirrorMode, drive.driveFolderId, drive.isLoading, listDriveFolder]);

  const fetchDriveFolder = useCallback(async () => {
    const folderId = extractDriveFolderId(driveFolderInput);
    if (!folderId) {
      fb.setError("Cole uma URL válida de pasta do Drive ou um Folder ID.");
      return;
    }
    await connectToDriveFolder(folderId, { resetState: true });
  }, [driveFolderInput, connectToDriveFolder]);

  const linkDriveFileToClient = useCallback(
    async ({ driveFile, client }) => {
      const wsId = String(workspaceKey || "").trim();
      const driveFileId = String(driveFile?.id || "").trim();
      const fileName = String(driveFile?.name || "").trim() || "Sem nome";
      const clientId = String(client?.id || "").trim();
      if (!wsId || !driveFileId || !clientId) return;
      const wsLabel = resolveWorkspaceLabel(activeWorkspace);
      drive.setTriagedDriveFileIds((prev) => {
        const base = prev instanceof Set ? prev : new Set();
        const next = new Set(base);
        next.add(driveFileId);
        return next;
      });
      try {
        const linkId = `${wsId}_${driveFileId}`;
        const data = await fetchGedLink(linkId);
        const existingParties = Array.isArray(data?.partiesRefs)
          ? data.partiesRefs.map(String)
          : [];
        const already = existingParties.includes(clientId);
        const partiesRefs = Array.from(
          new Set([...existingParties, clientId].filter(Boolean)),
        );
        await saveGedLink(linkId, {
          linkId,
          driveFileId,
          fileName,
          workspaceId: wsId,
          partiesRefs,
        });
        if (!already) {
          addClientNote(appId, clientId, {
            category: "SYSTEM",
            type: "DOC_UPLOADED",
            payload: {
              fileName: String(fileName || "").trim() || "Sem nome",
              text: "Documento vinculado ao acervo",
              workspaceId: wsId,
              workspaceLabel: wsLabel,
            },
            actor: { name: "Sistema", isBot: true },
          }).catch(() => null);
        }
        pushNotice("Sucesso: documento vinculado");
      } catch (e) {
        drive.setTriagedDriveFileIds((prev) => {
          const base = prev instanceof Set ? prev : new Set();
          const next = new Set(base);
          next.delete(driveFileId);
          return next;
        });
        pushNotice(`Falha ao vincular: ${String(e?.message || e)}`);
      }
    },
    [workspaceKey, pushNotice, activeWorkspace, resolveWorkspaceLabel],
  );

  const linkDriveFileToWorkspace = useCallback(
    async ({ driveFile, workspace }) => {
      const wsId = String(workspace?.id || "").trim();
      const driveFileId = String(driveFile?.id || "").trim();
      const fileName = String(driveFile?.name || "").trim() || "Sem nome";
      if (!wsId || !driveFileId) return;
      drive.setTriagedDriveFileIds((prev) => {
        const base = prev instanceof Set ? prev : new Set();
        const next = new Set(base);
        next.add(driveFileId);
        return next;
      });
      try {
        const wsLabel = resolveWorkspaceLabel(workspace);
        const primaryClientId = String(workspace?.clientId || "").trim();
        const refs = Array.isArray(workspace?.partiesRefs)
          ? workspace.partiesRefs
          : [];
        const extra = refs
          .map((p) =>
            typeof p === "string"
              ? String(p || "").trim()
              : String(p?.clientId || "").trim(),
          )
          .filter(Boolean);
        const parties = Array.from(
          new Set([primaryClientId, ...extra].filter(Boolean)),
        );
        const linkId = `${wsId}_${driveFileId}`;
        const existedData = await fetchGedLink(linkId);
        const existed = Boolean(Object.keys(existedData).length > 0);
        await saveGedLink(linkId, {
          linkId,
          driveFileId,
          fileName,
          workspaceId: wsId,
          partiesRefs: parties,
        });
        if (!existed) {
          try {
            parties.forEach((clientId) => {
              const cid = String(clientId || "").trim();
              if (!cid) return;
              addClientNote(appId, cid, {
                category: "SYSTEM",
                type: "DOC_UPLOADED",
                payload: {
                  fileName: String(fileName || "").trim() || "Sem nome",
                  text: "Documento vinculado ao acervo",
                  workspaceId: wsId,
                  workspaceLabel: wsLabel,
                },
                actor: { name: "Sistema", isBot: true },
              }).catch(() => null);
            });
          } catch {}
        }
        pushNotice("Sucesso: documento vinculado");
      } catch (e) {
        drive.setTriagedDriveFileIds((prev) => {
          const base = prev instanceof Set ? prev : new Set();
          const next = new Set(base);
          next.delete(driveFileId);
          return next;
        });
        pushNotice(`Falha ao vincular: ${String(e?.message || e)}`);
      }
    },
    [pushNotice, resolveWorkspaceLabel],
  );

  const handlePublishPetition = useCallback(
    async (file, meta) => {
      if (!file || !meta) return;
      if (petitionBusy) return;
      const MAX_PETITION_SIZE = 50 * 1024 * 1024;
      if (file.size > MAX_PETITION_SIZE) {
        const sizeMb = (file.size / 1024 / 1024).toFixed(1);
        pushNotice(
          `❌ Arquivo ${sizeMb}MB excede o limite de 50MB para petições.`,
        );
        return;
      }
      setPetitionBusy(true);
      const appIdVal = String(FIREBASE_CONFIG?.projectId || "").trim();
      const actorName =
        String(displayName || email || "Operador").trim() || "Operador";
      let sRefForRollback = null;
      let documentRefForRollback = null;
      let uploadCompleted = false;
      try {
        if (!appIdVal) throw new Error("ProjectId do Firebase não encontrado.");
        const primaryClientId = String(meta.clientId || "").trim();
        if (!primaryClientId)
          throw new Error(
            "Nenhum cliente vinculado ao Caderno de Autos. Vincule ao menos uma parte antes de publicar.",
          );
        const storage = getStorage();
        const safeTitle = String(meta.title || file.name || "Petição").trim();
        const safeName = String(file.name || "peticao.pdf").trim();
        const ext = safeName.includes(".")
          ? safeName.split(".").pop().toLowerCase()
          : "pdf";
        const ts = Date.now();
        const storageName = `PETICAO_${ts}_${safeName.replace(/[^a-zA-Z0-9._-]/g, "_")}`;
        const path = `artifacts/${appIdVal}/public/documents/petitions/${primaryClientId}/${storageName}`;
        const sRef = storageRef(storage, path);
        sRefForRollback = sRef;
        pushNotice("Calculando hash de autenticidade...");
        let authenticityHash = "";
        try {
          authenticityHash = await computeFileSHA256(file);
        } catch (hashErr) {
          console.warn("[RDA] Falha ao calcular SHA-256:", hashErr);
        }
        if (authenticityHash) {
          try {
            const existing = await checkDuplicatePetition(
              appIdVal,
              authenticityHash,
              primaryClientId,
            );
            if (existing) {
              const existingTitle = String(
                existing?.title || "Petição anterior",
              ).trim();
              const proceed = window.confirm(
                `⚠️ Petição idêntica já publicada para este cliente:\n\n"${existingTitle}"\n\n` +
                  `O arquivo possui o mesmo hash SHA-256. Deseja publicar mesmo assim?`,
              );
              if (!proceed) {
                pushNotice(
                  "Publicação cancelada — petição duplicada detectada.",
                );
                return;
              }
            }
          } catch (dupErr) {
            console.warn(
              "[RDA] UU-05 — falha ao verificar duplicata (seguindo fluxo):",
              dupErr,
            );
          }
        }
        const documentRef = await createPetitionIntent(appIdVal, {
          type: "PETITION",
          status: "UPLOADING",
          module: "petitions",
          title: safeTitle,
          clientId: primaryClientId,
          relatedEntities: [primaryClientId],
          storagePath: path,
          clientVisible: false,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          createdBy: email || null,
        });
        documentRefForRollback = documentRef;
        pushNotice("Publicando petição...");
        await uploadBytes(sRef, file, {
          contentType: file.type || "application/pdf",
        });
        uploadCompleted = true;
        const downloadUrl = await getDownloadURL(sRef);
        const commitPayload = {
          status: "pending_consent",
          name: safeTitle,
          fileName: storageName,
          fileUrl: downloadUrl,
          downloadUrl,
          storageEngine: {
            provider: "firebase_storage",
            access: "authenticated_url",
            mode: "pdf_archive",
            storagePath: path,
          },
          mimeType: file.type || "application/pdf",
          extension: ext,
          category: "Petição",
          classification: { category: "Petição" },
          clientVisible: true,
          requiresConsent: true,
          consentStatus: "pending_consent",
          cadernoId: String(meta.cadernoId || "").trim() || null,
          cadernoTitle: String(meta.cadernoTitle || "").trim() || null,
          context: {
            module: "petitions",
            documentKind: "PETITION_PUBLICATION",
            workspaceTitle: String(meta.cadernoTitle || "").trim(),
            slotLabel: String(meta.slotLabel || "").trim(),
            parteContraria: String(meta.parteContraria || "").trim(),
          },
          publication: {
            source: "caderno_de_autos",
            publishType: "petition",
            format: ext,
            version: 1,
            publishedAtIso: new Date().toISOString(),
            publishedBy: actorName,
            storageMode: "pdf_archive",
          },
          updatedAt: serverTimestamp(),
          updatedBy: email || null,
          ...(authenticityHash ? { authenticityHash } : {}),
        };
        await commitPetition(documentRef, commitPayload);
        try {
          await addPetitionNote(appIdVal, primaryClientId, {
            category: "SYSTEM",
            type: "PETITION_PUBLISHED",
            payload: {
              text: `Petição publicada para consentimento: ${safeTitle}`,
              html: `<p><strong>Petição publicada para aprovação:</strong> ${safeTitle}</p><p>Publicada por ${actorName}.</p><p>Status: Consentimento pendente.</p>`,
              documentId: documentRef.id,
              title: safeTitle,
              fileName: storageName,
              fileUrl: downloadUrl,
              downloadUrl,
              storagePath: path,
              workspaceLabel: "Petição",
              module: "petitions",
              requiresConsent: true,
              consentStatus: "pending_consent",
              format: ext,
            },
            actor: { name: actorName, isBot: false },
          });
        } catch (noteErr) {
          console.warn(
            "[RDA] Petição publicada mas falha ao registrar nota no prontuário:",
            noteErr,
          );
        }
        try {
          createOfficeNotification(
            "Petição Publicada",
            `"${safeTitle}" foi publicada no acervo do cliente e aguarda consentimento.`,
            "SYSTEM",
            null,
          );
        } catch (notifErr) {
          console.warn(
            "[RDA] Falha ao criar notificação de escritório:",
            notifErr,
          );
        }
        pushNotice(`✅ Petição "${safeTitle}" publicada com sucesso!`);
        modals.setPetitionModal(null);
      } catch (e) {
        if (documentRefForRollback) {
          try {
            await rollbackPetition(documentRefForRollback);
          } catch (rbErr) {
            console.warn(
              "[RDA] SAGA rollback — falha ao apagar Intent do Firestore:",
              rbErr,
            );
          }
        }
        if (uploadCompleted && sRefForRollback) {
          try {
            await deleteObject(sRefForRollback);
          } catch (rbErr) {
            if (rbErr?.code !== "storage/object-not-found") {
              console.warn(
                "[RDA] SAGA rollback — falha ao apagar arquivo do Storage:",
                rbErr,
              );
            }
          }
        }
        pushNotice(
          `Erro ao publicar petição: ${String(e?.message || e).slice(0, 120)}`,
        );
      } finally {
        setPetitionBusy(false);
      }
    },
    [petitionBusy, pushNotice],
  );

  const syncWorkspaceNow = useCallback(async () => {
    if (!workspaceDocRef) return;
    if (!isHydrated) return;
    if (syncBusy) return;
    if (hostMode === "word") return;
    setSyncBusy(true);
    try {
      const now = Date.now();
      const localAssignCount = Object.keys(assignments || {}).length;
      if (localAssignCount === 0) {
        try {
          const { data: live } = await fetchWorkspaceDoc(workspaceDocRef);
          const legacyAssignCount = Object.keys(
            live?.organizerState?.assignments || {},
          ).length;
          let maxPerPieceAssign = 0;
          const allStates = live?.organizerStates || {};
          for (const st of Object.values(allStates)) {
            const n = Object.keys(st?.assignments || {}).length;
            if (n > maxPerPieceAssign) maxPerPieceAssign = n;
          }
          if (legacyAssignCount > 0 || maxPerPieceAssign > 0) {
            console.warn(
              "[ANTI-WIPE] Recusando save: local.assignments=0 mas remoto tem legacy=" +
                legacyAssignCount +
                " e maxPerPiece=" +
                maxPerPieceAssign +
                ". Prevenindo regressão Aline.",
            );
            setSyncBusy(false);
            return;
          }
        } catch (guardErr) {
          console.warn(
            "[ANTI-WIPE] Falha ao ler estado remoto para guard — permitindo save:",
            guardErr,
          );
        }
      }
      const stateToSave = {
        assignments,
        slotNames,
        slotOrder,
        clientRequested,
        numeroInicialAnexos,
      };

      // FIX "CADERNO PULA PRA PRIMEIRA POSIÇÃO AO ABRIR" (22/08/2026):
      // causa raiz achada lendo este arquivo a fundo — este autosave grava
      // updatedAt:serverTimestamp() (linha ~2860) toda vez que dispara, e
      // "Ordenar: Modificado" (workspaceSortMode, CadernoCockpit.jsx) lê
      // esse MESMO campo. Como o guard de 2 renders (linha ~3020) só evita
      // o autosave disparar na hidratação inicial, mas não compara o
      // conteúdo em si, qualquer render subsequente que recrie
      // assignments/slotOrder como objeto novo (mesmo com o MESMO
      // conteúdo) contava como "mudou" — abrir/olhar um caderno já
      // existente bastava pra empurrá-lo pro topo da lista "Modificado",
      // dando a impressão de que os dados de outro caderno foram
      // substituídos ali (a POSIÇÃO mudava, não o dado). Comparação de
      // conteúdo contra lastConfirmedOrgStateRef (já existe, mesma
      // referência usada pelo guard ANTI-WIPE acima) — só grava (e só
      // stampa updatedAt) quando algo realmente mudou. Aditivo: não altera
      // nenhum caminho de escrita real, só adiciona um "return" mais cedo
      // quando não há edição de verdade.
      const confirmedOrgState = lastConfirmedOrgStateRef.current;
      const stateUnchanged =
        confirmedOrgState &&
        JSON.stringify(assignments) === JSON.stringify(confirmedOrgState.assignments || {}) &&
        JSON.stringify(slotNames) === JSON.stringify(confirmedOrgState.slotNames || {}) &&
        JSON.stringify(slotOrder) === JSON.stringify(confirmedOrgState.slotOrder || []) &&
        JSON.stringify(clientRequested) === JSON.stringify(confirmedOrgState.clientRequested || {}) &&
        // Sem isto, mudar SÓ o "começar em" seria tratado como "nada mudou" e não salvaria.
        // Peça antiga (sem o campo salvo) vale 1.
        numeroInicialAnexos === (confirmedOrgState.numeroInicialAnexos ?? 1);
      if (stateUnchanged) {
        setSyncBusy(false);
        return;
      }

      localOrganizerUpdatedAtRef.current = now;
      console.log(
        "[SYNC] Saving to Firestore (pieceKey:",
        effectivePieceKey,
        "):",
        JSON.stringify(stateToSave, null, 2),
      );
      const _cd = clientDataRef.current;
      const updatePayload = {
        ...(_cd.clientId
          ? {
              clientId: _cd.clientId,
              clientName: _cd.clientName,
              clientCpf: _cd.clientCpf,
              clientEmail: _cd.clientEmail,
            }
          : {}),
        // FIX CAMPO-FANTASMA (26/08/2026): ver comentário equivalente em
        // commitPieceType, acima neste mesmo arquivo — chave computada com
        // ponto não funciona com setDoc(merge:true), precisa vir aninhada.
        // Este é o autosave PRINCIPAL (dispara a cada edição de slot e no
        // botão "Salvar & Organizar") — era o call-site de maior impacto do
        // bug, confirmado gerando o campo-fantasma real no caso [CLIENTE_A]
        // [CLIENTE_A].
        ...(effectivePieceKey
          ? { organizerStates: { [effectivePieceKey]: stateToSave } }
          : {}),
        organizerState: stateToSave,
        organizerStateUpdatedAtMs: now,
        updatedAt: serverTimestamp(),
      };
      // ITENS B+D (26/08/2026): grava o documento principal E um retrato
      // imutável deste salvamento juntos, no mesmo writeBatch (tudo ou
      // nada) — o retrato fica em autos_workspaces/{id}/anexos_history/,
      // nunca é sobrescrito pelo próximo save, serve de "último bom
      // conhecido" pra conferir/restaurar se algo der errado depois.
      await saveWorkspaceDocWithHistory(workspaceDocRef, updatePayload, {
        pieceKey: effectivePieceKey || null,
        assignments,
        slotNames,
        slotOrder,
        numeroInicialAnexos,
      });
      lastConfirmedOrgStateRef.current = JSON.parse(
        JSON.stringify(stateToSave),
      );
      hasUnsavedChangesRef.current = false;
      fb.setNotice("Salvo no Firestore.");
      window.setTimeout(
        () =>
          fb.setNotice((cur) => (cur === "Salvo no Firestore." ? null : cur)),
        2000,
      );
    } catch (err) {
      console.error("[SYNC] Falha ao salvar:", err);
      const confirmedState = lastConfirmedOrgStateRef.current;
      if (confirmedState) {
        revertState(confirmedState);
        console.warn(
          "[SYNC] revertState() executado — UI revertida ao último estado confirmado",
        );
      }
      if (err?.code === "permission-denied") {
        fb.setError(
          "ACESSO NEGADO (403): O Firestore rejeitou a gravação. Verifique as regras de segurança e permissões do usuário.",
        );
      } else {
        fb.setError("Falha ao salvar: " + String(err?.message || err));
      }
    } finally {
      setSyncBusy(false);
    }
  }, [
    workspaceDocRef,
    isHydrated,
    syncBusy,
    hostMode,
    assignments,
    slotNames,
    slotOrder,
    clientRequested,
    numeroInicialAnexos,
    revertState,
    fb,
    lastConfirmedOrgStateRef,
    localOrganizerUpdatedAtRef,
    effectivePieceKey,
  ]);
  syncWorkspaceNowRef.current = syncWorkspaceNow;

  // ── Effects base ────────────────────────────────────────────────────────
  useEffect(() => {
    const isWord = Boolean(window?.Word);
    setHostMode(isWord ? "word" : "web");
  }, []);

  // Sincroniza workspacePartyRefs com activeWorkspace quando o caderno muda.
  // ANTES: só era populado no useState inicial — quando trocava de caderno, as partes
  // do NOVO workspace nunca chegavam ao POST /bridge/state → Word exibia "Nenhuma parte".
  // FIX 2: Se partiesRefs estiver vazio, usa o clientId + clientName do workspace como
  // parte padrão (cliente principal do caderno).
  useEffect(() => {
    const raw = Array.isArray(activeWorkspace?.partiesRefs)
      ? activeWorkspace.partiesRefs
      : [];
    const fromPartiesRefs = raw
      .map((p) => {
        if (typeof p === "string")
          return p.trim()
            ? {
                clientId: p.trim(),
                role: "Parte",
                name: "",
                resp: "",
                polo: "Terceiro Interessado",
              }
            : null;
        return {
          clientId: String(p?.clientId || "").trim(),
          role: String(p?.role || "").trim(),
          name: String(p?.name || "").trim(),
          resp: String(p?.resp || "").trim(),
          // FIX (22/08/2026): este mapeamento é a ORIGEM do bug — lia
          // activeWorkspace.partiesRefs (com "polo" já gravado) e
          // reconstruía cada item só com 4 campos, descartando "polo"
          // ANTES mesmo do efeito de autosave (linha ~3844) reescrever o
          // Firestore sem ele. As duas pontas precisavam do fix, não só
          // a que grava.
          polo: String(p?.polo || "").trim() || "Terceiro Interessado",
        };
      })
      .filter((p) => p && p.clientId);

    // Se partiesRefs veio vazio, deriva do clientId/clientName do workspace
    if (fromPartiesRefs.length === 0) {
      const wsClientId = String(activeWorkspace?.clientId || "").trim();
      const wsClientName = String(
        activeWorkspace?.clientName ||
          activeWorkspace?.clientNameSnapshot ||
          "",
      ).trim();
      if (wsClientId) {
        setWorkspacePartyRefs([
          {
            clientId: wsClientId,
            role: "Autor",
            name: wsClientName,
            resp: "",
            polo: "Polo Ativo",
          },
        ]);
        return;
      }
    }

    setWorkspacePartyRefs(fromPartiesRefs);
  }, [activeWorkspace?.id]);

  useEffect(() => {
    const next = String(activeWorkspace?.pieceType || "").trim();
    if (next) setPieceType(next);
  }, [activeWorkspace?.id, activeWorkspace?.pieceType]);

  useEffect(() => {
    autoConnectKeyRef.current = "";
    autoConnectInitialKeyRef.current = "";
  }, [isWorkspaceControlled, activeWorkspace?.id]);

  useEffect(() => {
    const onSync = () => syncWorkspaceNowRef.current?.();
    try {
      window.addEventListener("RDA_AUTOS_SYNC", onSync);
    } catch {}
    return () => {
      try {
        window.removeEventListener("RDA_AUTOS_SYNC", onSync);
      } catch {}
    };
  }, []);

  // FIX ECHO-GUARD ENVENENADO (27/08/2026): este efeito carimbava
  // localOrganizerUpdatedAtRef.current = Date.now() toda vez que
  // assignments/slotNames/slotOrder/clientRequested mudavam — por QUALQUER
  // motivo, inclusive quando a mudança vinha de setHydratedState() aplicando
  // dado REMOTO (uma correção feita direto no Firestore, fora do navegador).
  // Resultado: assim que a peça hidratava uma vez (mesmo com o cache local
  // do Firestore/IndexedDB, que pode ser velho), este ref ficava travado no
  // relógio de parede do carregamento da página — sempre MAIOR que o
  // timestamp de qualquer escrita remota feita ANTES daquele carregamento.
  // A comparação em "[MERGE] Ignorando echo do próprio save" (mais abaixo,
  // remoteUpdatedAt <= localUpdatedAt) então descartava toda atualização
  // remota genuína pelo resto da sessão daquela aba — sobrevivendo a Forçar
  // Reset e reinício de navegador, porque o problema nunca foi cache, era
  // esta comparação sempre errada. O carimbo legítimo de "acabei de salvar"
  // já existe no lugar certo (dentro de syncWorkspaceNow, ~linha 2884) — só
  // ali reflete uma gravação de verdade feita por este cliente. Achado no
  // caderno [CLIENTE_A], após a reconciliação de anexos nunca
  // aparecer na tela mesmo confirmada correta no banco 3 vezes seguidas.


  useEffect(() => {
    console.count('[INSTR-MC57] effect fired');
    if (!isHydrated) return;
    if (!workspaceDocRef) return;
    if (!workspaceKey) return;
    if (hostMode === "word") return;
    // MC57 (03/09/2026, achado real: log/gravação infinitos só ao trocar
    // pra uma peça diferente da 1ª, nunca na 1ª peça do caderno) — a trava
    // "pula a 1ª escrita" era only por CADERNO (workspaceKey), não por
    // PEÇA. Resultado: ela só protegia a 1ª peça carregada; trocar pra
    // qualquer peça seguinte já entrava com a trava "gasta", então a
    // hidratação inicial da peça nova (dado vindo do Firestore, não uma
    // edição do titular) disparava esta mesma gravação de volta —
    // ping-pong com o listener onSnapshot. Chave agora inclui a peça.
    const clientRequestedInitKey = `${workspaceKey}::${effectivePieceKey}`;
    if (clientRequestedInitKeyRef.current !== clientRequestedInitKey) {
      clientRequestedInitKeyRef.current = clientRequestedInitKey;
      return;
    }
    // MC57b: o espelho pro campo LEGADO (nível do caderno, sem prefixo de
    // peça) só faz sentido quando a peça ativa é a única/original do
    // caderno — exatamente o mesmo raciocínio já aplicado à recuperação de
    // legado (ver legacyRecoveryElegivel mais abaixo). Sem essa trava,
    // criar/abrir QUALQUER peça nova sobrescrevia esse campo compartilhado
    // com o slotOrder/clientRequested da peça nova (geralmente vazia),
    // "roubando" o dado da peça original (Petição Inicial) que outros
    // pontos do código ainda leem dali — daí a lista da Petição Inicial
    // piscando/regravando sem parar ao criar "Nova Peça".
    const totalPiecesForLegacyMirror = Array.isArray(activeWorkspace?.pieces)
      ? activeWorkspace.pieces.length
      : 0;
    const t = window.setTimeout(() => {
      // FIX D-parte2 (03/09/2026, RELATORIO_DIAGNOSTICO_LOOP_INFINITO_TROCA_PECA,
      // Camada 3): esta escrita nunca carimbava organizerStateUpdatedAtMs
      // (o único campo que o guard de eco compara) — só updatedAt:
      // serverTimestamp(), um campo diferente. Resultado: mesmo depois do
      // guard proteger os 4 campos (Fix D-parte1, mais abaixo no arquivo),
      // o eco ESPECÍFICO desta escrita continuava passando batido, porque
      // não havia como o guard reconhecer "fui eu quem escreveu isso".
      // Mesmo padrão já usado em syncWorkspaceNow (~linha 2913/2941).
      const nowMc57 = Date.now();
      localOrganizerUpdatedAtRef.current = nowMc57;
      patchWorkspaceDoc(workspaceDocRef, {
        ...(totalPiecesForLegacyMirror <= 1
          ? {
              "organizerState.clientRequested": clientRequested || {},
              "organizerState.slotOrder": slotOrder || [],
            }
          : {}),
        ...(effectivePieceKey
          ? {
              [`organizerStates.${effectivePieceKey}.clientRequested`]:
                clientRequested || {},
              [`organizerStates.${effectivePieceKey}.slotOrder`]:
                slotOrder || [],
            }
          : {}),
        organizerStateUpdatedAtMs: nowMc57,
        updatedAt: serverTimestamp(),
      }).catch(() => null);
    }, 400);
    return () => window.clearTimeout(t);
  }, [
    clientRequested,
    isHydrated,
    workspaceDocRef,
    workspaceKey,
    effectivePieceKey,
    hostMode,
    slotOrder,
    // FIX REGRESSÃO (03/09/2026, achado ao vivo depois do A+B+C+D): usar o
    // ARRAY `activeWorkspace?.pieces` inteiro como dependência é o próprio
    // tipo de bug que este relatório inteiro investigou — o array é
    // reconstruído (nova referência) a cada snapshot do documento do
    // workspace, MESMO quando o conteúdo de `pieces` não mudou (qualquer
    // outro campo do doc que mude já basta). Isso fazia este efeito
    // reagendar a escrita (400ms) infinitamente, mesmo na 1ª peça — pior
    // que o bug original, que só afetava peça 2+. Corrigido: depender só
    // da CONTAGEM (valor primitivo, estável), que é tudo que o corpo do
    // efeito realmente usa (totalPiecesForLegacyMirror).
    activeWorkspace?.pieces?.length,
  ]);

  useEffect(() => {
    // FIX GATILHO AUTOMÁTICO 3 (26/08/2026): este efeito disparava
    // RDA_AUTOS_SYNC 700ms após qualquer mudança em assignments/slotOrder/
    // clientRequested, gravando no Firestore por fora do botão "Salvar &
    // Organizar" — rodando em paralelo com o autosave/rename do próprio
    // botão e com o mecanismo de recuperação (HYDRATE-RECOVERY), correndo o
    // risco de persistir um slotOrder temporariamente inconsistente. Decisão
    // (26/08/2026, com o titular): nada grava sozinho — só "Salvar &
    // Organizar" grava, sempre em lote (ver handleMasterSyncAll/
    // syncWorkspaceNow). Este efeito agora só marca "há alteração não salva"
    // (para o aviso de fechar a aba, useEffect de beforeunload abaixo) — não
    // dispara mais escrita nenhuma.
    //
    // HISTÓRICO (por que esse autosave existia): antes de 22/08/2026, só
    // reagia a `clientRequested` — vincular um anexo novo (assignToSlot) ou
    // reordenar (handleDragEnd) mudavam `assignments`/`slotOrder` mas nunca
    // `clientRequested`, então essas duas ações só ficavam persistidas ao
    // clicar manualmente em "Salvar & Organizar", e se o site caísse antes
    // desse clique, a mudança se perdia. O aviso de beforeunload cobre esse
    // mesmo risco agora, sem precisar gravar sozinho no meio da edição.
    if (!isHydrated) return;
    if (clientRequestedSyncKeyRef.current !== workspaceKey) {
      clientRequestedSyncKeyRef.current = workspaceKey;
      clientRequestedSyncRef.current = false;
      hasUnsavedChangesRef.current = false;
      return;
    }
    if (!clientRequestedSyncRef.current) {
      clientRequestedSyncRef.current = true;
      return;
    }
    hasUnsavedChangesRef.current = true;
  }, [clientRequested, assignments, slotOrder, numeroInicialAnexos, isHydrated, workspaceKey]);

  // Aviso ao fechar/recarregar a aba com alteração não salva — substitui a
  // proteção que o autosave automático (removido acima) dava antes.
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (!hasUnsavedChangesRef.current) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);

  useEffect(() => {
    console.count('[INSTR-ENRICH] fired');
    if (!Array.isArray(partyDetails) || partyDetails.length === 0) return;
    setWorkspacePartyRefs((prev) => {
      if (!Array.isArray(prev) || prev.length === 0) return prev;
      let changed = false;
      const next = prev.map((p) => {
        const cid = String(p?.clientId || "").trim();
        const detail = partyDetails.find(
          (d) => String(d?.clientId || d?.id || "").trim() === cid,
        );
        let updated = p;
        if (!String(p?.name || "").trim()) {
          const name = String(detail?.name || "").trim();
          if (name) {
            updated = { ...updated, name };
            changed = true;
          }
        }
        if (!String(p?.resp || "").trim()) {
          const respFromCrm = String(detail?.resp || "").trim();
          if (respFromCrm) {
            updated = { ...updated, resp: respFromCrm };
            changed = true;
          }
        }
        return updated;
      });
      return changed ? next : prev;
    });
  }, [partyDetails]);

  useEffect(() => {
    if (isHydrated) return;
    if (workspaceHydratedKeyRef.current) return;
    const expected = checklist.checklist.map((_, idx) => `slot-${idx}`);
    setSlotOrder((prev) => {
      if (
        Array.isArray(prev) &&
        prev.length === expected.length &&
        expected.every((id) => prev.includes(id))
      )
        return prev;
      console.log(
        "[CHECKLIST-EFFECT] Resetting slotOrder to default (not yet hydrated)",
      );
      return expected;
    });
  }, [checklist.checklist, isHydrated]);

  useEffect(() => {
    if (!checklist.checklist?.length) return;
    if (!drive.driveFiles?.length) return;
    const driveIdSet = new Set(drive.driveFiles.map((f) => String(f.id)));
    const driveById = new Map(drive.driveFiles.map((f) => [String(f.id), f]));
    setAssignments((prev) => {
      const next = { ...(prev || {}) };
      const used = new Set(
        Object.values(next || {})
          .map((v) => String(v?.id || "").trim())
          .filter(Boolean),
      );
      let changed = false;
      for (let i = 0; i < checklist.checklist.length; i += 1) {
        const slotId = `slot-${i}`;
        const label = String(checklist.checklist[i] || "").trim();
        const pinnedId = label ? pinned.pinnedFiles?.[label] : null;
        if (!pinnedId) continue;
        if (!driveIdSet.has(String(pinnedId))) continue;
        if (
          used.has(String(pinnedId)) &&
          String(next?.[slotId]?.id || "").trim() !== String(pinnedId)
        )
          continue;
        if (String(next?.[slotId]?.id || "") !== String(pinnedId)) {
          const file = driveById.get(String(pinnedId));
          next[slotId] = {
            id: String(pinnedId),
            name: String(file?.name || ""),
            url: String(file?.webViewLink || ""),
          };
          used.add(String(pinnedId));
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [checklist.checklist, drive.driveFiles, pinned.pinnedFiles]);

  useEffect(() => {
    console.count('[INSTR-PARTYDETAILS] fired');
    if (!workspaceKey) {
      setPartyDetails([]);
      return;
    }
    const refs = Array.isArray(workspacePartyRefs) ? workspacePartyRefs : [];
    if (!refs.length) {
      setPartyDetails([]);
      return;
    }
    let alive = true;
    const run = async () => {
      try {
        if (isDev) {
          console.log("[Partes] partiesRefs:", refs);
        }
        const results = await Promise.all(
          refs.map(async (ref) => {
            const decodeUnicodeEscapes = (s) =>
              String(s || "").replace(/\\u([0-9a-fA-F]{4})/g, (_, h) =>
                String.fromCharCode(parseInt(h, 16)),
              );
            const clientId = String(ref?.clientId || "").trim();
            const role = decodeUnicodeEscapes(String(ref?.role || "").trim());
            if (!clientId) return null;

            // --- DIAGNÓSTICO P19: fetchClientDoc ---
            const rawClient = await fetchClientDoc(appId, clientId);

            // Verifica 2 condições separadas:
            //   (A) Documento simplesmente não existe no Firestore
            const docNotFound = rawClient?.name === "CLIENTE NÃO ENCONTRADO";
            //   (B) Documento existe mas não tem qualification sub-object
            const docExistsNoQual = !docNotFound && !rawClient?.qualification;

            let canonical;
            let hydrated;

            if (docNotFound) {
              // (A) Documento não existe — fallback via cache
              const cachedClient = Array.isArray(cache.clientsCache)
                ? cache.clientsCache.find(
                    (c) => String(c?.id || "").trim() === clientId,
                  )
                : null;
              if (cachedClient && cachedClient?.qualification) {
                canonical =
                  await hydrateAndBuildCanonicalClientQualification(
                    cachedClient,
                  );
                hydrated = canonical?.client || cachedClient;
              } else {
                // Fallback final: monta um objeto mínimo a partir do nome da ref
                const fallbackName = String(
                  ref?.name || ref?.clientId || "[Cliente]",
                ).trim();
                hydrated = {
                  id: clientId,
                  name: fallbackName,
                  type: CLIENT_TYPES.PHYSICAL,
                  qualification: {
                    physical: {
                      name: fallbackName,
                      nationality: "",
                      rg: "",
                      cpf: "",
                    },
                  },
                };
                canonical = {
                  client: hydrated,
                  text: `<strong>${fallbackName}</strong>`,
                };
              }
            } else if (docExistsNoQual) {
              // (B) Documento existe mas sem qualification — gerar qualificação mínima diretamente
              const rawName = String(
                rawClient?.name ||
                  rawClient?.displayName ||
                  ref?.name ||
                  "[Cliente]",
              ).trim();
              const rawCpf = String(
                rawClient?.cpf ||
                  rawClient?.document ||
                  rawClient?.qualification?.physical?.cpf ||
                  "",
              ).trim();
              const rawCpfFormatted = rawCpf
                ? rawCpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4")
                : "";
              hydrated = {
                id: clientId,
                name: rawName,
                type: CLIENT_TYPES.PHYSICAL,
                qualification: {
                  physical: {
                    name: rawName,
                    nationality: "",
                    rg: "",
                    cpf: rawCpf,
                  },
                },
                contact: rawClient?.contact || {},
                location: rawClient?.location || {},
              };
              const qualText = `<strong>${rawName.toUpperCase()}</strong>${rawCpfFormatted ? `, CPF ${rawCpfFormatted}` : ""}`;
              canonical = {
                client: hydrated,
                text: qualText,
              };
            } else {
              // (C) Documento existe com qualification completa — normalizar type antes
              const normalized = {
                ...rawClient,
                type:
                  rawClient?.type === "Pessoa Física" ||
                  rawClient?.type === "PHYSICAL" ||
                  rawClient?.type === "physical"
                    ? CLIENT_TYPES.PHYSICAL
                    : rawClient?.type === "Pessoa Jurídica" ||
                        rawClient?.type === "LEGAL" ||
                        rawClient?.type === "legal"
                      ? CLIENT_TYPES.LEGAL
                      : rawClient?.type || CLIENT_TYPES.PHYSICAL,
              };
              canonical =
                await hydrateAndBuildCanonicalClientQualification(normalized);
              hydrated = canonical?.client || normalized;
            }

            const qualificationRaw =
              canonical?.text || "[Qualificação não gerada]";
            const qualificationText = String(
              stripHtmlTags(qualificationRaw) || "",
            ).trim();
            const ooxml = wrapInOOXML(qualificationText);
            const _nt = String(hydrated?.type || "")
              .normalize("NFD")
              .replace(/[\u0300-\u036f]/g, "")
              .toLowerCase();
            const isAnimal = _nt.includes("animal");
            const isMinorOrUnborn =
              _nt.includes("minor") ||
              _nt.includes("nasc") ||
              _nt.includes("unborn");
            const isLegalOrCondominium =
              _nt.includes("legal") ||
              _nt.includes("condo") ||
              _nt.includes("bankrupt") ||
              _nt.includes("espolio") ||
              _nt.includes("estate") ||
              _nt.includes("massa");
            const safeName =
              (isAnimal
                ? String(hydrated?.qualification?.animal?.name || "").trim()
                : isMinorOrUnborn
                  ? String(
                      hydrated?.qualification?.minor?.name ||
                        hydrated?.qualification?.physical?.name ||
                        hydrated?.name ||
                        "",
                    ).trim()
                  : isLegalOrCondominium
                    ? String(
                        hydrated?.qualification?.legal?.companyName ||
                          hydrated?.qualification?.legal?.company_name ||
                          hydrated?.name ||
                          "",
                      ).trim()
                    : String(
                        hydrated?.qualification?.physical?.socialName ||
                          hydrated?.qualification?.physical?.name ||
                          hydrated?.name ||
                          "",
                      ).trim()) || clientId;
            const respName = String(
              hydrated?.qualification?.animal?.responsible?.name ||
                hydrated?.qualification?.animal?.guardian?.name ||
                hydrated?.qualification?.legal?.representative?.name ||
                hydrated?.qualification?.minor?.representative?.name ||
                hydrated?.qualification?.estate?.inventorier?.name ||
                hydrated?.anchor?.name ||
                "",
            ).trim();
            const healedRole = isAnimal && role === "Autor" ? "Animal" : role;
            return {
              id: clientId,
              clientId,
              role: healedRole,
              originalRole: role,
              name: safeName,
              resp: respName,
              text: qualificationText,
              ooxml,
            };
          }),
        );
        if (!alive) return;
        const filtered = results.filter(Boolean);
        if (isDev) {
          console.log(
            "[Partes] partyDetails OK:",
            filtered.map((p) => ({
              id: p.id,
              role: p.role,
              name: p.name,
              textLen: String(p.text || "").length,
            })),
          );
        }
        // CAMADA 7 (03/09/2026, PROMPT_TCREI_LOOP_INFINITO_TROCA_PECA_
        // SEGUNDA_OPINIAO): era o único setState deste arquivo que
        // trocava a referência do estado INCONDICIONALMENTE, mesmo com
        // conteúdo idêntico — `results` sempre monta objetos NOVOS (mapeia
        // `refs` de novo a cada disparo). Isso mantinha a referência de
        // `partyDetails` sempre nova, o que mantinha o efeito "ENRICH"
        // (deps: [partyDetails]) e, por tabela, o efeito "Bridge" (que lê
        // partyDetails no corpo) sempre re-executando mesmo sem nenhum
        // dado ter mudado de verdade. Todo outro setState deste arquivo já
        // segue o padrão "só troca a referência se o conteúdo mudou" — este
        // era a exceção.
        setPartyDetails((prev) => {
          try {
            if (JSON.stringify(prev) === JSON.stringify(filtered)) return prev;
          } catch {}
          return filtered;
        });
      } catch (e) {
        try {
          console.warn(
            "[Partes] Falha ao montar partyDetails (Firestore/hydrate/OOXML):",
            e,
          );
        } catch {}
        if (!alive) return;
        setPartyDetails([]);
      }
    };
    run();
    return () => {
      alive = false;
    };
  }, [workspaceKey, workspacePartyRefs]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const onPointerDownCapture = (e) => {
      const target = e?.target;
      if (!target) return;
      const partyEl = partyOmniboxRef.current;
      const linkEl = linkOmniboxRef.current;
      if (partyEl && partyEl.contains(target)) return;
      if (linkEl && linkEl.contains(target)) return;
      search.setPartyOpen(false);
      search.setLinkOpen(false);
      const jurisdictionEl = metaJurisdictionRef?.current;
      if (jurisdictionEl && !jurisdictionEl.contains(target))
        meta.setMetaJurisdictionOpen(false);
    };
    const onKeyDownCapture = (e) => {
      if (e?.key !== "Escape") return;
      search.setPartyOpen(false);
      search.setLinkOpen(false);
      meta.setMetaJurisdictionOpen(false);
    };
    document.addEventListener("mousedown", onPointerDownCapture, true);
    document.addEventListener("touchstart", onPointerDownCapture, true);
    window.addEventListener("keydown", onKeyDownCapture, true);
    return () => {
      document.removeEventListener("mousedown", onPointerDownCapture, true);
      document.removeEventListener("touchstart", onPointerDownCapture, true);
      window.removeEventListener("keydown", onKeyDownCapture, true);
    };
  }, []);

  useEffect(() => {
    if (!isWorkspaceControlled) return;
    // MC55 (03/09/2026, achado real: botão "Abrir no Drive" sempre abrindo
    // a pasta da 1ª peça, mesmo com a Contestação ativa): resolvedAttachments
    // FolderId (pasta PRÓPRIA da peça ativa) tem que vencer systemInitial
    // FolderId (prop herdada de activeWorkspace.attachmentsFolderId — nível
    // do CADERNO, não da peça, sempre a mesma independente de qual peça
    // está selecionada). A ordem antiga colocava o valor do caderno
    // primeiro, então a resolução por peça (já existia, já correta) nunca
    // tinha chance de vencer. Também faltava activePieceRecordId nas deps —
    // sem isso, trocar de peça dentro do MESMO caderno nunca reavaliava.
    const raw = String(
      resolvedAttachmentsFolderId ||
        systemInitialFolderId ||
        activeWorkspace?.driveFolderInput ||
        activeWorkspace?.driveFolderId ||
        "",
    ).trim();
    if (raw) setDriveFolderInput(raw);
  }, [
    isWorkspaceControlled,
    activeWorkspace?.id,
    activePieceRecordId,
    systemInitialFolderId,
    resolvedAttachmentsFolderId,
  ]);

  useEffect(() => {
    if (!isMirrorMode) return;
    // MC55: mesma correção de prioridade do efeito acima.
    const fid = String(
      resolvedAttachmentsFolderId ||
        systemInitialFolderId ||
        activeWorkspace?.driveFolderId ||
        "",
    ).trim();
    if (fid) drive.setDriveFolderId(fid);
  }, [
    isMirrorMode,
    activeWorkspace?.id,
    activePieceRecordId,
    systemInitialFolderId,
    resolvedAttachmentsFolderId,
  ]);

  useEffect(() => {
    const cur = String(drive.selectedDriveFileId || "").trim();
    if (
      cur &&
      pendingDriveFiles.some((f) => String(f?.id || "").trim() === cur)
    )
      return;
    const next = pendingDriveFiles[0]?.id
      ? String(pendingDriveFiles[0].id)
      : "";
    drive.setSelectedDriveFileId(next);
  }, [pendingDriveFiles, drive.selectedDriveFileId]);

  useEffect(() => {
    try {
      localStorage.setItem(
        "rda_taskpane_width_mode",
        String(uiWidthMode || "compact"),
      );
    } catch {}
  }, [uiWidthMode]);

  useEffect(() => {
    saveJson("LS_PINNED_FILES", pinned.pinnedFiles || {});
  }, [pinned.pinnedFiles]);

  useEffect(() => {
    if (!stateKey) return;
    if (!checklist.checklist?.length) return;
    if (!isHydrated) return;
    saveJson(stateKey, { assignments, slotNames, slotOrder, clientRequested, numeroInicialAnexos });
  }, [
    stateKey,
    isHydrated,
    assignments,
    slotNames,
    slotOrder,
    clientRequested,
    numeroInicialAnexos,
    checklist.checklist?.length,
  ]);

  useEffect(() => {
    if (!pieceType || !appId || !isHydrated) return;
    fetchTemplate(appId, pieceType)
      .then((data) => {
        if (!data) return;
        if (
          Array.isArray(data.checklist_override) &&
          data.checklist_override.length > 0
        ) {
          checklist.setChecklist(data.checklist_override);
        }
        if (
          data.slotNames_override &&
          typeof data.slotNames_override === "object"
        ) {
          // FIX RACE TROCA-DE-PEÇA (14/09/2026): `hasExisting` lia `slotNames`
          // do CLOSURE do efeito (capturado no momento em que `pieceType`
          // mudou), não o valor atual. Ao voltar pra uma peça já visitada,
          // esta promise costuma resolver DEPOIS do hydrate real do
          // Firestore (organizerStates[pieceKey]) já ter repovoado
          // `slotNames` corretamente — mas o closure antigo ainda via a
          // peça como "vazia" (era o snapshot de quando a troca começou) e
          // sobrescrevia os nomes reais pelo template salvo antigo
          // (`slotNames_override`, um snapshot menor/desatualizado de
          // quando "Salvar como Padrão" foi usado). Achado ao vivo (Chrome
          // real + CDP): slotNames caía de 58 pra 45 chaves ~100-400ms
          // depois do hydrate correto, sempre que a mesma peça era
          // revisitada. Fix: ler o valor VIVO da store no momento em que a
          // promise resolve, não o capturado na criação do efeito.
          const liveSlotNames = useOrganizerStore.getState().slotNames;
          const hasExisting = Object.keys(liveSlotNames || {}).some(
            (k) => liveSlotNames[k],
          );
          if (!hasExisting) {
            setSlotNames(data.slotNames_override);
          }
        }
      })
      .catch(() => null);
  }, [pieceType, appId, isHydrated]);

  useEffect(() => {
    if (!stateKey) return;
    if (!checklist.checklist?.length) return;
    if (!isHydrated) return;
    const settings = window?.Office?.context?.document?.settings;
    if (!settings) return;
    try {
      settings.set("RDA_State", {
        workspaceId: workspaceKey || null,
        stateKey,
        data: { assignments, slotNames, slotOrder },
      });
      settings.saveAsync();
    } catch {}
  }, [stateKey, workspaceKey, isHydrated, assignments, slotNames, slotOrder]);

  // FIX REGRESSÃO (03/09/2026, mesmo padrão do MC57 — RELATORIO_
  // DIAGNOSTICO_LOOP_INFINITO_TROCA_PECA): o efeito "Bridge" logo abaixo
  // dependia do ARRAY `activeWorkspace?.partes?.adversas` direto — nova
  // referência a cada snapshot do doc do workspace, mesmo sem conteúdo
  // mudar de verdade. Assinatura (string) em vez do array: só muda quando
  // o CONTEÚDO muda de verdade.
  const adversasSignature = stableStringify(
    activeWorkspace?.partes?.adversas || [],
  );

  useEffect(() => {
    console.count('[INSTR-BRIDGE] fired');
    if (!workspaceKey) return;
    if (!isHydrated) return;
    if (isMirrorMode) return;
    let baseParties =
      Array.isArray(partyDetails) && partyDetails.length > 0
        ? partyDetails
        : [];
    // Se partyDetails falhou, busca dados do cliente no CRM automaticamente
    if (baseParties.length === 0) {
      const refs = Array.isArray(workspacePartyRefs) ? workspacePartyRefs : [];
      if (refs.length > 0) {
        baseParties = refs.map((p) => {
          const nome = String(
            activeWorkspace?.clientName ||
              activeWorkspace?.clientNameSnapshot ||
              p?.name ||
              "",
          ).trim();
          // Tenta buscar documento do workspace
          let docAutor =
            activeWorkspace?.clientCpf ||
            activeWorkspace?.clientCnpj ||
            activeWorkspace?.metadata?.cpf ||
            activeWorkspace?.metadata?.cnpj ||
            "";
          const apenasNumeros = String(docAutor).replace(/\D/g, "");
          const sufixo =
            apenasNumeros.length === 14
              ? `, CNPJ ${docAutor}`
              : apenasNumeros.length === 11
                ? `, CPF ${docAutor}`
                : "";
          return {
            ...p,
            name: nome,
            text: docAutor ? nome + sufixo : nome,
            ooxml: docAutor
              ? `<p><b>${nome}</b>${sufixo}</p>`
              : `<p><b>${nome}</b></p>`,
          };
        });
      }
    }
    // ULTIMO RECURSO: se tudo vazio, usa clientId do workspace como fallback
    if (baseParties.length === 0 && activeWorkspace?.clientId) {
      const cid = String(activeWorkspace.clientId).trim();
      if (cid) {
        const nomeAutor = String(
          activeWorkspace?.clientName ||
            activeWorkspace?.clientNameSnapshot ||
            "",
        ).trim();
        // Busca CPF/CNPJ do workspace para qualificar o Autor
        let docAutor =
          activeWorkspace?.clientCpf ||
          activeWorkspace?.clientCnpj ||
          activeWorkspace?.metadata?.cpf ||
          activeWorkspace?.metadata?.cnpj ||
          "";
        const apenasNumeros = String(docAutor).replace(/\D/g, "");
        const sufixo =
          apenasNumeros.length === 14
            ? `, CNPJ ${docAutor}`
            : apenasNumeros.length === 11
              ? `, CPF ${docAutor}`
              : "";
        const text = nomeAutor + sufixo;
        baseParties = [
          {
            clientId: cid,
            role: "Autor",
            name: nomeAutor,
            text: text,
            ooxml: `<p><b>${nomeAutor}</b>${sufixo}</p>`,
            resp: "",
          },
        ];
      }
    }
    // INCLUI CO-PARTES (partes adversas)
    const coPartesRaw = Array.isArray(activeWorkspace?.partes?.adversas)
      ? activeWorkspace.partes.adversas
      : [];
    const coPartesNormalized = coPartesRaw
      .filter((p) => p && (p.clientId || p.id || p.name || p.nome))
      .map((p) => {
        const _name = String(p?.nome || p?.name || "").trim();
        const _tipo = String(p?.tipo || "PF").trim();
        const _doc = String(p?.documento || "").trim();
        const _rawText = String(p?.text || "").trim();
        // Gera text básico quando parte adversa não tem qualificação
        const _genText = _rawText
          ? _rawText
          : _tipo === "PF"
            ? `${_name}${_doc ? `, CPF ${_doc}` : ""}`
            : _tipo === "PJ"
              ? `${_name}${_doc ? `, CNPJ ${_doc}` : ""}`
              : `${_name}${_doc ? `, doc. ${_doc}` : ""}`;
        return {
          clientId: String(p?.clientId || p?.id || "").trim(),
          id: String(p?.clientId || p?.id || "").trim(),
          role: String(p?.role || "Réu").trim() || "Réu",
          name: _name,
          resp: String(p?.resp || "").trim(),
          text: _genText,
          ooxml: String(p?.ooxml || wrapInOOXML(_genText)).trim(),
          isOfficeClient: false,
        };
      });
    // Deduplica
    const _seenIds = new Set(
      baseParties
        .map((p) => String(p?.clientId || p?.id || "").trim())
        .filter(Boolean),
    );
    for (const _cp of coPartesNormalized) {
      if (_cp.clientId && !_seenIds.has(_cp.clientId)) {
        baseParties.push(_cp);
        _seenIds.add(_cp.clientId);
      }
    }

    // ═══ FIX P16: Cria array NOVO unificado em vez de mutar baseParties ═══
    // Evita: (a) deduplicação falha quando coParte não tem clientId;
    //        (b) perda de parte adversa sem vínculo CRM (nome/text puro).
    const _allParties = [];
    const _seenKeys = new Set();
    for (const _p of baseParties) {
      const _key = String(_p?.clientId || _p?.id || "").trim();
      if (_key && _seenKeys.has(_key)) continue;
      if (_key) _seenKeys.add(_key);
      _allParties.push(_p);
    }
    for (const _cp of coPartesNormalized) {
      const _key = String(_cp?.clientId || _cp?.id || "").trim();
      if (_key && _seenKeys.has(_key)) continue;
      if (_key) _seenKeys.add(_key);
      // Se não tem clientId nem id (parte adversa digitada manualmente), usa name
      // como chave para evitar duplicatas, mas SEMPRE inclui
      if (!_key) {
        const _nameKey = String(_cp?.name || "")
          .trim()
          .toLowerCase();
        if (_nameKey && _seenKeys.has(_nameKey)) continue;
        if (_nameKey) _seenKeys.add(_nameKey);
      }
      _allParties.push(_cp);
    }

    if (isDev) {
      console.log(
        "[Bridge-DEBUG] workspacePartyRefs:",
        Array.isArray(workspacePartyRefs) ? workspacePartyRefs.length : 0,
      );
      console.log(
        "[Bridge-DEBUG] partyDetails:",
        Array.isArray(partyDetails) ? partyDetails.length : 0,
      );
      console.log("[Bridge-DEBUG] coPartes:", coPartesRaw.length);
      console.log("[Bridge-DEBUG] _allParties (total):", _allParties.length);
    }
    const bridgeParties = _allParties.map((p) => ({
      id: String(p?.id || p?.clientId || "").trim(),
      role: String(p?.role || "").trim(),
      name: String(p?.name || "").trim(),
      text: String(p?.text || "").trim(),
      ooxml: String(p?.ooxml || "").trim(),
    }));
    const payload = {
      workspaceId: workspaceKey,
      state: {
        assignments: assignments || {},
        driveFiles: Array.isArray(drive.driveFiles) ? drive.driveFiles : [],
        checklist: Array.isArray(checklist.checklist)
          ? checklist.checklist
          : [],
        slotOrder: Array.isArray(slotOrder) ? slotOrder : [],
        slotNames: slotNames && typeof slotNames === "object" ? slotNames : {},
        // FIX V22 (21/07/2026): RESTAURADO parties com qualificacao.
        // O erro foi remover parties daqui — este watcher e o UNICO ponto
        // que envia parties para a bridge na inicializacao do caderno.
        // O subscriber rda:client-updated SO e chamado quando o CRM publica.
        // Para evitar sobrescrita: usar partyDetails que ja contem a
        // qualificacao gerada pelo hydrateAndBuildCanonicalClientQualification.
        parties: bridgeParties,
        pieceType: String(pieceType || "").trim(),
        // Numeração dos anexos (24/09/2026): o Word imprime estes rótulos prontos.
        numeroInicialAnexos,
        rotulosPorSlot: rotulosDosAnexos,
      },
    };
    try {
      if (bridgePostTimerRef.current) {
        clearTimeout(bridgePostTimerRef.current);
      }
    } catch {}
    bridgePostTimerRef.current = setTimeout(() => {
      try {
        if (isDev) {
          console.log(
            "[Bridge] Enviando /bridge/state parties:",
            bridgeParties.map((p) => ({
              id: p.id,
              role: p.role,
              name: p.name,
            })),
          );
        }
      } catch {}
      try {
        const _wsKey = workspaceKey;
        if (_wsKey && bridgeParties.length > 0) {
          const partiesDocRef = doc(
            db,
            "artifacts",
            appId,
            "public",
            "data",
            "autos_workspaces",
            _wsKey,
            "bridge",
            "parties",
          );
          setDoc(partiesDocRef, {
            parties: bridgeParties,
            updatedAt: serverTimestamp(),
          }).catch(function (err) {
            console.error("[Bridge/Parties FS] Erro:", err);
          });
        }
      } catch (e) {
        console.error("[Bridge/Parties FS] Erro:", e);
      }
      postBridgeStateRef.current?.(payload);
    }, 400);
    return () => {
      try {
        if (bridgePostTimerRef.current) {
          clearTimeout(bridgePostTimerRef.current);
          bridgePostTimerRef.current = null;
        }
      } catch {}
    };
  }, [
    workspaceKey,
    isHydrated,
    isMirrorMode,
    assignments,
    drive.driveFiles,
    checklist.checklist,
    slotOrder,
    bridgeForceCounter,
    workspacePartyRefs,
    pieceType,
    adversasSignature,
    numeroInicialAnexos,
    rotulosDosAnexos,
  ]);

  // ── Scroll to top on workspace change ────────────────────────────────────
  useEffect(() => {
    if (!workspaceKey) return;
    if (workspaceHydratedKey !== workspaceKey) return;
    const scrollToTop = () => {
      const el = document.getElementById("rda-main-scroll-container");
      if (el) el.scrollTop = 0;
      window.scrollTo({ top: 0, behavior: "instant" });
    };
    scrollToTop();
    const t = setTimeout(scrollToTop, 150);
    return () => clearTimeout(t);
  }, [workspaceKey, workspaceHydratedKey]);

  useEffect(() => {
    const scrollToTop = () => {
      const el = document.getElementById("rda-main-scroll-container");
      if (el) el.scrollTop = 0;
      window.scrollTo({ top: 0, behavior: "instant" });
    };
    scrollToTop();
    const t = setTimeout(scrollToTop, 80);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      runLinkSearch(search.linkSearch);
    }, 300);
    return () => clearTimeout(t);
  }, [search.linkSearch, runLinkSearch]);

  useEffect(() => {
    const t = setTimeout(() => {
      runClientSearch(search.partySearch, search.setPartyResults);
    }, 300);
    return () => clearTimeout(t);
  }, [search.partySearch, runClientSearch]);

  useEffect(() => {
    if (!workspaceDocRef) return;
    if (!workspaceKey) return;
    if (hostMode === "word") return;
    const t = setTimeout(() => {
      const folderId = extractDriveFolderId(driveFolderInput);
      const payload = {
        driveFolderInput: driveFolderInput ? String(driveFolderInput) : null,
        driveFolderId: folderId ? String(folderId) : null,
        updatedAt: serverTimestamp(),
      };
      saveWorkspaceDoc(workspaceDocRef, payload).catch(() => null);
    }, 700);
    return () => clearTimeout(t);
  }, [workspaceDocRef, workspaceKey, hostMode, driveFolderInput]);

  // CAMADA 6 (03/09/2026, PROMPT_TCREI_LOOP_INFINITO_TROCA_PECA_SEGUNDA_OPINIAO):
  // este efeito era o ÚNICO ponto do arquivo que grava no documento do
  // workspace SEM NENHUM guard de eco — nem o `organizerStateUpdatedAtMs`
  // caseiro (Camada 2/3, aplicado a assignments/slotOrder/etc), nem
  // comparação de conteúdo antes de agendar a escrita. Achado real: a
  // branch "1ª vez hidratando esta peça" (mais abaixo, ~linha 4478) sempre
  // troca `workspacePartyRefs` por uma referência NOVA a cada troca de
  // peça — mesmo quando o conteúdo é idêntico, porque `partiesRefs` é um
  // campo de nível CADERNO (não por-peça), igual em qualquer peça do mesmo
  // caso. Isso disparava este efeito (deps: workspacePartyRefs) a CADA
  // troca de peça, gravando de novo no Firestore mesmo sem nada ter
  // mudado de verdade — e cada gravação sem filtro de eco é candidata a
  // realimentar o onSnapshot (Camada 1: `hasPendingWrites:false` do próprio
  // ACK do servidor não é filtrado por Fix A, só o `hasPendingWrites:true`
  // otimista é). Corrigido em 2 frentes: (a) não agenda escrita nenhuma se
  // o conteúdo já é idêntico ao último gravado por este mesmo efeito
  // (assinatura via JSON.stringify, comparação na hora de AGENDAR, não só
  // no callback do onSnapshot); (b) quando grava de verdade, carimba
  // `organizerStateUpdatedAtMs` (mesmo campo que a Camada 2/3 já usa) para
  // que o merge de workspacePartyRefs na branch "já hidratado" (mais
  // abaixo) reconheça e ignore o eco desta escrita especificamente.
  useEffect(() => {
    if (!workspaceDocRef) return;
    if (!workspaceKey) return;
    if (hostMode === "word") return;
    // [FIX P14] Só salva partiesRefs se o subscription do Firestore já confirmou os dados
    // Evita race condition: effect de fallback (activeWorkspace?.partiesRefs vazio) salva
    // vazio no Firestore ANTES do subscription chegar com os dados reais.
    const refs = Array.isArray(workspacePartyRefs) ? workspacePartyRefs : [];
    if (refs.length === 0) return;
    const normalizedRefs = refs
      .map((p) => ({
        clientId: String(p?.clientId || "").trim(),
        role: String(p?.role || "").trim(),
        name: String(p?.name || "").trim(),
        resp: String(p?.resp || "").trim(),
        // FIX (22/08/2026): este autosave (dispara sempre que a aba
        // Caderno de Autos monta) tinha allowlist própria, incompatível
        // com a de CadernoCockpit.jsx/handleSaveCoPartes — apagava o
        // campo "polo" (padrão PJe/CNJ) toda vez que o titular só ABRIA
        // essa aba, sem editar nada. Mesma classe de bug já corrigida
        // em handleSaveCoPartes/handleSavePartes, achada tarde demais
        // porque cada allowlist vive num arquivo diferente.
        polo: String(p?.polo || "").trim() || "Terceiro Interessado",
      }))
      .filter((p) => p.clientId);
    const nextSig = JSON.stringify(normalizedRefs);
    if (nextSig === lastSavedPartiesRefsSigRef.current) return;
    const t = setTimeout(() => {
      const nowPartiesRefs = Date.now();
      lastSavedPartiesRefsSigRef.current = nextSig;
      localOrganizerUpdatedAtRef.current = nowPartiesRefs;
      const payload = {
        partiesRefs: normalizedRefs,
        organizerStateUpdatedAtMs: nowPartiesRefs,
        updatedAt: serverTimestamp(),
      };
      saveWorkspaceDoc(workspaceDocRef, payload).catch(() => null);
    }, 600);
    return () => clearTimeout(t);
  }, [workspaceDocRef, workspaceKey, hostMode, workspacePartyRefs]);

  useEffect(() => {
    if (!stateKey) {
      if (!workspaceHydratedKeyRef.current) {
        resetOrganizerStore();
      }
      return;
    }
    if (!isHydrated) {
      if (workspaceHydratedKeyRef.current) return;
      if (checklist.checklist.length) {
        const expected = checklist.checklist.map((_, idx) => `slot-${idx}`);
        setSlotOrder(expected);
      }
    }
  }, [
    stateKey,
    checklist.checklist,
    isHydrated,
    resetOrganizerStore,
    setSlotOrder,
  ]);

  useEffect(() => {
    if (!stateKey) return;
    if (!checklist.checklist.length) return;
    if (isHydrated) return;
    if (workspaceHydratedKeyRef.current) return;
    // FIX CACHE-FANTASMA (26/08/2026): quando existe um Caderno de Autos real
    // (workspaceKey preenchido), o Firestore É a fonte canônica — mesmo padrão
    // já usado por Contratos/Procurações, que não têm cache local algum. Antes,
    // este preview local rodava mesmo com workspaceKey presente sempre que
    // `drive.driveFiles` ainda estava vazio (ex.: listagem do Drive falhando/
    // lenta), e podia "vencer" a hidratação real do Firestore que chegava
    // depois — assignments/slotNames velhos do localStorage (de antes de
    // qualquer correção feita direto no banco) reapareciam na tela mesmo após
    // fechar e reabrir o navegador (localStorage sobrevive a isso; só "Forçar
    // Reset" limpa). Caso real: caderno [CLIENTE_A], reconciliação
    // de anexos sendo mascarada por este cache 3 vezes seguidas na mesma
    // sessão. Preview local agora só roda na ausência de workspace real.
    if (workspaceKey) return;
    const expected = checklist.checklist.map((_, idx) => `slot-${idx}`);
    const tryHydrateFromOfficeSettings = async () => {
      try {
        const settings = window?.Office?.context?.document?.settings;
        if (!settings) return null;
        const refreshAsync = settings.refreshAsync;
        if (typeof refreshAsync === "function") {
          await new Promise((resolve) => {
            refreshAsync(() => resolve(null));
          });
        }
        const raw = settings.get("RDA_State");
        if (!raw) return null;
        if (raw && typeof raw === "object" && raw.data) {
          if (
            raw.workspaceId &&
            workspaceKey &&
            String(raw.workspaceId) !== String(workspaceKey)
          )
            return null;
          if (
            raw.stateKey &&
            stateKey &&
            String(raw.stateKey) !== String(stateKey)
          )
            return null;
          return raw.data;
        }
        return raw;
      } catch {
        return null;
      }
    };
    const hydrate = async () => {
      const officeSaved = await tryHydrateFromOfficeSettings();
      if (officeSaved) {
        setHydratedState(officeSaved);
        return;
      }
      const saved = loadJson(stateKey);
      if (saved) {
        const nextOrder = Array.isArray(saved.slotOrder)
          ? saved.slotOrder.map(String)
          : [];
        const normalizedOrder =
          nextOrder.length === expected.length &&
          expected.every((id) => nextOrder.includes(id))
            ? nextOrder
            : expected;
        const nextNames =
          saved.slotNames && typeof saved.slotNames === "object"
            ? { ...saved.slotNames }
            : {};
        const rawAssignments =
          saved.assignments && typeof saved.assignments === "object"
            ? { ...saved.assignments }
            : {};
        if (!drive.driveFiles.length) {
          const seen = new Set();
          const deduped = {};
          normalizedOrder.forEach((sid) => {
            const rawVal = rawAssignments[sid];
            const fid =
              typeof rawVal === "string"
                ? String(rawVal).trim()
                : String(rawVal?.id || "").trim();
            if (!fid) return;
            if (seen.has(fid)) return;
            seen.add(fid);
            if (typeof rawVal === "string") {
              deduped[sid] = { id: fid, name: "", url: "" };
            } else {
              deduped[sid] = { ...(rawVal || {}), id: fid };
            }
          });
          setHydratedState({
            assignments: deduped,
            slotNames: nextNames,
            slotOrder: normalizedOrder,
            clientRequested: saved.clientRequested || {},
            numeroInicialAnexos: saved.numeroInicialAnexos,
          });
        } else {
          // NUNCA descartar um assignment salvo só porque esta listagem específica
          // do Drive não trouxe o arquivo (fetch parcial/timing/falha transitória).
          // A listagem só é usada para REFRESCAR nome/link quando disponível; a
          // remoção real de um vínculo só acontece por ação explícita do usuário
          // (handleResetSlot). Ver histórico: assignments válidos (ANEXO 20-29,
          // caso [CLIENTE_A] [CLIENTE_A]) eram apagados silenciosamente por este guard.
          const driveById = new Map(
            drive.driveFiles.map((f) => [String(f.id), f]),
          );
          const cleanAssignments = {};
          const seen = new Set();
          normalizedOrder.forEach((sid) => {
            const rawVal = rawAssignments[sid];
            const fid =
              typeof rawVal === "string"
                ? String(rawVal).trim()
                : String(rawVal?.id || "").trim();
            if (!fid) return;
            if (seen.has(fid)) return;
            seen.add(fid);
            const file = driveById.get(fid);
            const prevName =
              typeof rawVal === "string" ? "" : String(rawVal?.name || "");
            const prevUrl =
              typeof rawVal === "string" ? "" : String(rawVal?.url || "");
            cleanAssignments[sid] = {
              id: fid,
              name: String(file?.name || prevName),
              url: String(file?.webViewLink || prevUrl),
            };
          });
          setHydratedState({
            assignments: cleanAssignments,
            slotNames: nextNames,
            slotOrder: normalizedOrder,
            clientRequested: saved.clientRequested || {},
            numeroInicialAnexos: saved.numeroInicialAnexos,
          });
        }
      } else {
        setHydratedState({
          assignments: {},
          slotNames: {},
          slotOrder: expected,
          clientRequested: {},
        });
      }
    };
    hydrate();
  }, [
    stateKey,
    workspaceKey,
    checklist.checklist,
    drive.driveFiles,
    isHydrated,
    setHydratedState,
  ]);

  const onHostBridgeRef = useRef(onHostBridge);
  useEffect(() => {
    onHostBridgeRef.current = onHostBridge;
  }, [onHostBridge]);

  const bridgeKeyRef = useRef("");
  useEffect(() => {
    if (typeof onHostBridgeRef.current !== "function") return;
    const key = JSON.stringify({
      pieceType,
      isWorkspaceControlled,
      syncBusy,
      isRenaming,
      isHydrated,
      isMirrorMode,
      driveFolderId: drive.driveFolderId,
      assignments,
      slotNames,
      // FIX (28/08/2026): slotOrder faltava aqui — arrastar-e-soltar SÓ muda
      // slotOrder (assignments/slotNames continuam com o mesmo conteúdo,
      // só a ordem das chaves na tela muda). Sem slotOrder na chave, essa
      // key ficava idêntica antes/depois do reordenamento, o efeito abaixo
      // nunca reenviava um handleSaveAll (=handleMasterSyncAll) atualizado
      // pro CadernoCockpit, e o botão "Salvar & Organizar" continuava
      // chamando uma versão presa ao slotOrder de ANTES do drag — por isso
      // o Drive nunca era renomeado para refletir a nova ordem.
      slotOrder,
    });
    if (bridgeKeyRef.current === key) return;
    bridgeKeyRef.current = key;
    const t = setTimeout(() => {
      onHostBridgeRef.current({
        pieceType,
        isWorkspaceControlled,
        syncBusy,
        isRenaming,
        isHydrated,
        isMirrorMode,
        driveFolderId: drive.driveFolderId,
        driveUrl: drive.driveFolderId
          ? `https://drive.google.com/drive/folders/${drive.driveFolderId}`
          : "",
        PINNED_PIECE_TYPES,
        PIECE_OPTGROUPS_V2,
        commitPieceType,
        applyPieceTypeLabel,
        renamePieceFolderOnDrive,
        setClientData,
        handleSaveAll: handleMasterSyncAll,
        openNuke: () => {
          console.log("[NUKE] openNuke called");
          setNukeText("");
          setNukeOpen(true);
        },
        assignments,
        slotNames,
      });
    }, 0);
    return () => clearTimeout(t);
  }, [
    pieceType,
    isWorkspaceControlled,
    syncBusy,
    isRenaming,
    isHydrated,
    isMirrorMode,
    drive.driveFolderId,
    assignments,
    slotNames,
    slotOrder,
  ]);

  useEffect(() => {
    if (!autoConnect) return;
    if (!workspaceKey) return;
    if (workspaceHydratedKey !== workspaceKey) return;
    if (drive.isLoading) return;
    // MC55 (03/09/2026): mesma correção de prioridade — pasta da PEÇA ativa
    // sempre antes da pasta do CADERNO (systemInitialFolderId, herdada de
    // activeWorkspace.attachmentsFolderId lá em AutosDashboard.jsx, a mesma
    // pra qualquer peça selecionada).
    const raw = String(
      resolvedAttachmentsFolderId ||
        systemInitialFolderId ||
        activeWorkspace?.driveFolderId ||
        activeWorkspace?.driveFolderInput ||
        "",
    ).trim();
    const wsFolderId = extractDriveFolderId(raw);
    const fallbackFolderId = extractDriveFolderId(driveFolderInput);
    const folderId = wsFolderId || fallbackFolderId;
    if (!folderId) return;
    // Já conectado exatamente a este folder (não apenas "algum" folder já
    // carregado) — sem isso, trocar de caderno (ou de PEÇA — cada peça tem
    // sua própria pasta agora) nunca reconectava porque o guard antigo
    // bastava ter QUALQUER folder/lista já carregada.
    if (String(drive.driveFolderId || "") === folderId) return;
    const key = `${workspaceKey}::${effectivePieceKey}::${folderId}`;
    if (autoConnectKeyRef.current === key) return;
    autoConnectKeyRef.current = key;
    connectToDriveFolder(folderId, { resetState: false });
  }, [
    autoConnect,
    workspaceKey,
    workspaceHydratedKey,
    drive.driveFolderId,
    drive.isLoading,
    systemInitialFolderId,
    resolvedAttachmentsFolderId,
    effectivePieceKey,
    activeWorkspace?.driveFolderId,
    activeWorkspace?.driveFolderInput,
    driveFolderInput,
    connectToDriveFolder,
  ]);

  // CAMADA 8 (03/09/2026, PROMPT_TCREI_LOOP_INFINITO_TROCA_PECA_SEGUNDA_
  // OPINIAO — causa raiz real do loop, achada só com o navegador ao vivo,
  // depois de instrumentar connectToDriveFolder diretamente: este efeito e
  // o efeito acima (MC55) são DOIS mecanismos de auto-connect
  // independentes que, antes desta correção, compartilhavam o MESMO ref
  // (`autoConnectKeyRef`) como trava de "já conectei". Cada efeito grava
  // SUA PRÓPRIA chave nesse ref compartilhado — então toda vez que o efeito
  // acima conecta na pasta DA PEÇA, ele apaga a chave que ESTE efeito tinha
  // gravado (e vice-versa). Resultado, confirmado ao vivo (log
  // [INSTR-CONNECTDRIVE]) numa peça cuja pasta própria difere da pasta do
  // caderno: os 2 efeitos ficam se desmentindo — cada um vê
  // `drive.driveFolderId` apontando pra pasta do OUTRO, acha que "ainda não
  // conectei na minha pasta" (porque sua trava foi apagada pelo vizinho) e
  // reconecta, apagando a trava do outro de volta — ciclo sem fim, a cada
  // ~500-700ms, batendo folder A → folder B → folder A → ... indefinidamente.
  // Isso é 100% independente do mecanismo de eco do Firestore (Camadas
  // 1-7) — explica por que corrigir aquelas não bastou. Por que só afeta
  // peça 2+: a peça 1 (original, antes de existir "Nova Peça") tem
  // resolvedAttachmentsFolderId igual a systemInitialFolderId — os 2
  // efeitos convergem pro MESMO folderId, sem conflito. Corrigido em 2
  // frentes: (a) ref próprio (autoConnectInitialKeyRef), pra um efeito
  // nunca mais apagar a trava do outro; (b) este efeito agora recua por
  // completo quando existe uma pasta própria da PEÇA diferente da pasta do
  // caderno — ele é só um fallback pra quando a peça NÃO tem pasta própria,
  // nunca deveria competir pela mesma variável de estado com o efeito que
  // já resolve isso (o de cima já inclui systemInitialFolderId na própria
  // cadeia de fallback quando resolvedAttachmentsFolderId está vazio).
  useEffect(() => {
    if (!autoConnect) return;
    if (!workspaceKey) return;
    if (workspaceHydratedKey !== workspaceKey) return;
    if (!systemInitialFolderId) return;
    // Peça tem pasta própria diferente da pasta do caderno — o efeito
    // acima (MC55) já cuida disso; este efeito não deve competir.
    const ownPieceFolderId = extractDriveFolderId(
      String(resolvedAttachmentsFolderId || "").trim(),
    );
    if (
      ownPieceFolderId &&
      ownPieceFolderId !== extractDriveFolderId(systemInitialFolderId)
    )
      return;
    if (
      drive.driveFolderId &&
      String(drive.driveFolderId).trim() ===
        String(systemInitialFolderId).trim()
    )
      return;
    if (drive.isLoading) return;
    const key = `initial::${systemInitialFolderId}`;
    if (autoConnectInitialKeyRef.current === key) return;
    autoConnectInitialKeyRef.current = key;
    connectToDriveFolder(systemInitialFolderId, { resetState: false });
  }, [
    autoConnect,
    workspaceKey,
    workspaceHydratedKey,
    systemInitialFolderId,
    resolvedAttachmentsFolderId,
    drive.driveFolderId,
    drive.isLoading,
    connectToDriveFolder,
  ]);

  useEffect(() => {
    if (!workspaceDocRef) {
      workspaceHydratedKeyRef.current = "";
      pieceHydratedKeyRef.current = "";
      setWorkspaceHydratedKey("");
      setHydratedWorkspaceKeyStore(null);
      resetOrganizerStore();
      return;
    }

    const pieceScopedKey = `${workspaceKey}::${effectivePieceKey}`;

    const unsub = subscribeWorkspaceDoc(
      workspaceDocRef,
      (snap) => {
        if (!snap.exists()) {
          workspaceHydratedKeyRef.current = workspaceKey;
          pieceHydratedKeyRef.current = pieceScopedKey;
          setWorkspaceHydratedKey(workspaceKey);
          setHydratedWorkspaceKeyStore(workspaceKey);
          return;
        }

        const data = snap.data() || {};
        const nextDriveInput = String(
          data.driveFolderInput || data.driveFolderId || "",
        ).trim();
        const nextPartyRefsRaw = Array.isArray(data?.partiesRefs)
          ? data.partiesRefs
          : [];
        const nextPartyRefs = nextPartyRefsRaw
          .map((p) => {
            if (typeof p === "string") {
              const cid = p.trim();
              return cid
                ? {
                    clientId: cid,
                    role: "Parte",
                    name: "",
                    resp: "",
                    polo: "Terceiro Interessado",
                  }
                : null;
            }
            return {
              clientId: String(p?.clientId || "").trim(),
              role: String(p?.role || "").trim(),
              name: String(p?.name || "").trim(),
              resp: String(p?.resp || "").trim(),
              // FIX (22/08/2026): 3ª cópia do mesmo mapeamento estreito
              // achada nesta investigação — esta é a mais crítica das 3,
              // porque roda no listener onSnapshot AO VIVO (dispara em
              // toda escrita no Firestore, inclusive as feitas por script
              // fora do navegador) — descartava "polo" antes mesmo do
              // autosave ter chance de rodar.
              polo: String(p?.polo || "").trim() || "Terceiro Interessado",
            };
          })
          .filter((p) => p && p.clientId);

        if (pieceScopedKey !== pieceHydratedKeyRef.current) {
          if (nextDriveInput) {
            setDriveFolderInput(nextDriveInput);
          }
          // CAMADA 6b (03/09/2026, PROMPT_TCREI_LOOP_INFINITO_TROCA_PECA_
          // SEGUNDA_OPINIAO): esta branch roda em TODA troca de peça (é a
          // "1ª vez hidratando esta peça"), mesmo quando `partiesRefs` é
          // idêntico ao de antes — porque é campo de nível CADERNO, não
          // por-peça, igual pra qualquer peça do mesmo caso. Sem o
          // `partyRefsEqual`, toda troca de peça gerava uma referência NOVA
          // pra `workspacePartyRefs`, disparando o efeito PARTYDETAILS
          // (refetch no CRM), o ENRICH e o write-back (linha ~4058) sem
          // necessidade nenhuma. Mesmo guard já usado na branch "já
          // hidratado" logo abaixo.
          setWorkspacePartyRefs((prev) => {
            if (nextPartyRefs.length === 0 && prev.length > 0) {
              return prev;
            }
            return partyRefsEqual(prev, nextPartyRefs) ? prev : nextPartyRefs;
          });
          localOrganizerUpdatedAtRef.current = Number(
            data?.organizerStateUpdatedAtMs || Date.now(),
          );
          const perPieceState = effectivePieceKey
            ? data?.organizerStates?.[effectivePieceKey]
            : null;
          const legacyState = data?.organizerState;
          // MC54 (03/09/2026, pedido do titular): a recuperação de legado
          // abaixo (window.confirm + o "else" silencioso mais adiante) só
          // faz sentido no cenário ORIGINAL que ela foi construída pra
          // resolver — caderno de peça ÚNICA, de antes do "Nova Peça"
          // existir. Com 2+ peças no mesmo caderno, o campo legado (nível
          // do caderno, não da peça) já pertence à peça original — oferecer
          // ou aplicar esse mesmo legado pra QUALQUER peça nova e vazia
          // sempre "vaza" os anexos da peça original pra dentro da peça
          // nova, mesmo com confirmação explícita do titular (achado real:
          // Contestação herdou os 3 primeiros anexos da Petição Inicial).
          // Titular confirmou: peças diferentes do mesmo caso NUNCA devem
          // se oferecer pra herdar documento uma da outra.
          const totalPieces = Array.isArray(data?.pieces)
            ? data.pieces.length
            : 0;
          const legacyRecoveryElegivel = totalPieces <= 1;
          const _perPieceHasData =
            perPieceState &&
            typeof perPieceState === "object" &&
            (Object.keys(perPieceState.assignments || {}).length > 0 ||
              Object.keys(perPieceState.slotNames || {}).length > 0 ||
              (Array.isArray(perPieceState.slotOrder) &&
                perPieceState.slotOrder.length > 0));
          let remoteOrgState;
          if (_perPieceHasData) {
            const perPieceAssignEmpty =
              !perPieceState.assignments ||
              Object.keys(perPieceState.assignments).length === 0;
            const legacyAssignMap =
              legacyState?.assignments &&
              typeof legacyState.assignments === "object"
                ? legacyState.assignments
                : null;
            const legacyHasAssignments =
              legacyAssignMap && Object.keys(legacyAssignMap).length > 0;
            if (perPieceAssignEmpty && legacyHasAssignments && legacyRecoveryElegivel) {
              // FIX HYDRATE-RECOVERY (26/08/2026, item F da estratégia
              // definitiva): antes, o merge era aplicado direto e ficava
              // marcado como "confirmado" (lastConfirmedOrgStateRef), pronto
              // pra ser persistido no próximo "Salvar & Organizar" sem o
              // titular saber que a lista veio de uma recuperação, não de
              // uma ação sua. Agora pergunta antes de aplicar — "não" mantém
              // a peça genuinamente vazia (mesmo comportamento do ramo
              // "else" logo abaixo, quando não há legado nenhum pra
              // recuperar).
              const confirmMerge = window.confirm(
                "Esta peça está sem documentos vinculados, mas há dados de upload de cliente que podem pertencer a ela.\n\n" +
                  "Usar esses dados agora? (Confira se são mesmo deste caso antes de confirmar — clique Cancelar se não tiver certeza.)",
              );
              if (!confirmMerge) {
                console.log(
                  "[HYDRATE-RECOVERY] Recuperação recusada pelo titular — peça mantida vazia.",
                );
                remoteOrgState = perPieceState;
                fb.setNotice(
                  "Recuperação automática recusada — peça mantida vazia. Vincule os anexos manualmente.",
                );
                window.setTimeout(() => {
                  fb.setNotice((cur) =>
                    cur && cur.startsWith("Recuperação automática recusada")
                      ? null
                      : cur,
                  );
                }, 15000);
              } else {
              console.log(
                "[HYDRATE-RECOVERY] perPiece.assignments vazio; mesclando legacy.assignments (confirmado pelo titular)",
              );
              remoteOrgState = {
                ...perPieceState,
                assignments: legacyAssignMap,
              };
              // AVISO VISÍVEL (Opção B, 23/08/2026): usa o mesmo mecanismo
              // de aviso em tela já existente (fb.setNotice — a faixa que
              // aparece no topo do FileHubArea), em vez de só console.log
              // que ninguém vê. Some sozinho em 15s.
              fb.setNotice(
                "Documentos recuperados do campo de upload do cliente, conforme confirmado.",
              );
              window.setTimeout(() => {
                fb.setNotice((cur) =>
                  cur && cur.startsWith("Documentos recuperados")
                    ? null
                    : cur,
                );
              }, 15000);
              // Flag de auditoria não-destrutiva (grep-ável depois via
              // scripts/checklist "validação olímpica" — não interfere em
              // nada que já é salvo).
              saveWorkspaceDoc(workspaceDocRef, {
                lastRecoveryMergeAt: Date.now(),
                lastRecoveryMergeInfo: {
                  pieceKey: effectivePieceKey,
                  mergedAssignmentKeys: Object.keys(legacyAssignMap),
                },
              }).catch((e) =>
                console.warn(
                  "[HYDRATE-RECOVERY] Falha ao gravar flag de auditoria (não crítico):",
                  e,
                ),
              );
              }
            } else if (perPieceAssignEmpty) {
              // FIX Opção B (23/08/2026): removido o scan cross-peça
              // (HYDRATE-DEEP-RECOVERY) que buscava assignments em QUALQUER
              // outra peça do mesmo workspace, sem checar se pertenciam ao
              // mesmo caso. Foi esse mecanismo que permitiu a contaminação
              // cruzada de cliente no caderno [PET_A] — Constituição
              // Art. 14.6/14.7. Peça sem assignments e sem legado = vazia
              // de verdade, sem tentar "adivinhar" de outra peça.
              console.log(
                "[HYDRATE] Nenhum assignment disponível na peça nem no legacy — genuinamente vazia",
              );
              remoteOrgState = perPieceState;
            } else {
              remoteOrgState = perPieceState;
            }
          } else {
            // MC54: mesma regra acima — peça sem NENHUM dado próprio só
            // herda o legado do caderno se for a única peça do workspace.
            remoteOrgState = legacyRecoveryElegivel ? legacyState : perPieceState;
          }
          console.log(
            "[HYDRATE] Firestore pieceKey:",
            effectivePieceKey,
            "organizerState:",
            JSON.stringify(remoteOrgState, null, 2),
          );
          if (
            remoteOrgState &&
            typeof remoteOrgState === "object" &&
            Object.keys(remoteOrgState).length > 0
          ) {
            setHydratedState(remoteOrgState);
            lastConfirmedOrgStateRef.current = JSON.parse(
              JSON.stringify(remoteOrgState),
            );
            hasUnsavedChangesRef.current = false;
            console.log(
              "[HYDRATE] setHydratedState applied. slotOrder:",
              remoteOrgState.slotOrder,
            );
          } else {
            // FIX B (03/09/2026, RELATORIO_DIAGNOSTICO_LOOP_INFINITO_TROCA_PECA,
            // Camada 4): markHydrated() é no-op depois que isHydrated já é
            // true (a partir da 1ª peça que carrega no caderno) — trocar
            // pra uma peça genuinamente nova/vazia não limpava a store,
            // que continuava com slotOrder/assignments da peça ANTERIOR.
            // setHydratedState({}) é o mesmo normalizador usado 8 linhas
            // acima, só que zera de verdade (confirmado: peças de teste
            // criadas hoje ficaram com 29 slots idênticos à 1ª peça, com
            // 0 assignments — herança fantasma, não coincidência).
            setHydratedState({});
            lastConfirmedOrgStateRef.current = null;
            console.log("[HYDRATE] Workspace virgem — setHydratedState({}) aplicado");
          }
          workspaceHydratedKeyRef.current = workspaceKey;
          pieceHydratedKeyRef.current = pieceScopedKey;
          setWorkspaceHydratedKey(workspaceKey);
          setHydratedWorkspaceKeyStore(workspaceKey);
          return;
        }

        if (!driveFolderInputRef.current && nextDriveInput) {
          setDriveFolderInput(nextDriveInput);
        }

        const remoteUpdatedAt = Number(data?.organizerStateUpdatedAtMs || 0);
        const localUpdatedAt = Number(localOrganizerUpdatedAtRef.current || 0);
        const isOwnWriteEcho =
          remoteUpdatedAt > 0 && remoteUpdatedAt <= localUpdatedAt;

        // CAMADA 6 (03/09/2026, PROMPT_TCREI_LOOP_INFINITO_TROCA_PECA_
        // SEGUNDA_OPINIAO): este merge de workspacePartyRefs rodava
        // INCONDICIONALMENTE em todo disparo do onSnapshot — inclusive
        // ecos de escrita própria (Fix A só filtra o snapshot otimista
        // hasPendingWrites:true; o snapshot de confirmação do servidor,
        // hasPendingWrites:false, ainda passa e ainda roda esta branch).
        // Era o único merge desta branch sem NENHUMA proteção de eco (nem
        // sequer o organizerStateUpdatedAtMs). Agora reaproveita o mesmo
        // guard usado por assignments/slotOrder/slotNames/clientRequested
        // logo abaixo — o efeito de write-back de partiesRefs (linha
        // ~4058) agora carimba esse mesmo campo ao gravar.
        if (!isOwnWriteEcho) {
          setWorkspacePartyRefs((prev) => {
            if (nextPartyRefs.length === 0) {
              return prev;
            }
            return partyRefsEqual(prev, nextPartyRefs) ? prev : nextPartyRefs;
          });
        }
        partySubscriptionConfirmedRef.current = nextPartyRefs.length > 0;

        if (isOwnWriteEcho) {
          console.log(
            "[MERGE] Ignorando echo do próprio save (remote:",
            remoteUpdatedAt,
            "local:",
            localUpdatedAt,
            ")",
          );
        } else {
          const _pieceState = effectivePieceKey
            ? data?.organizerStates?.[effectivePieceKey]
            : null;
          const _pieceHasAssignments =
            _pieceState?.assignments &&
            typeof _pieceState.assignments === "object" &&
            Object.keys(_pieceState.assignments).length > 0;
          const remoteAssignments = _pieceHasAssignments
            ? _pieceState.assignments
            : data?.organizerState?.assignments;
          if (remoteAssignments && typeof remoteAssignments === "object") {
            setAssignments((localAssign) => {
              let changed = false;
              const merged = { ...(localAssign || {}) };
              for (const [slotId, remote] of Object.entries(
                remoteAssignments,
              )) {
                if (
                  remote?.id &&
                  (!merged[slotId] || merged[slotId].id !== remote.id)
                ) {
                  merged[slotId] = remote;
                  changed = true;
                  console.log(
                    "[MERGE] Novo assignment remoto:",
                    slotId,
                    remote.id,
                  );
                } else if (
                  remote?.id &&
                  merged[slotId] &&
                  !merged[slotId].multiFiles &&
                  Array.isArray(remote.multiFiles)
                ) {
                  merged[slotId] = {
                    ...merged[slotId],
                    multiFiles: remote.multiFiles,
                  };
                  changed = true;
                }
              }
              return changed ? merged : localAssign;
            });
          }
        }

        const remoteLocks = data?._uploadLocks;
        if (remoteLocks && typeof remoteLocks === "object") {
          setUploadLocks((prev) => {
            const next = { ...remoteLocks };
            return JSON.stringify(prev) !== JSON.stringify(next) ? next : prev;
          });
        }

        // FIX D (03/09/2026, RELATORIO_DIAGNOSTICO_LOOP_INFINITO_TROCA_PECA,
        // Camada 2): o bloco de slotOrder/slotNames/clientRequested abaixo
        // rodava incondicionalmente, mesmo quando o guard de eco logo acima
        // (linha ~4616) já tinha identificado "isto é eco da minha própria
        // escrita" e pulado o merge de assignments por causa disso — só
        // assignments era protegido, os 3 campos irmãos não. Mesma
        // condição, reaproveitada aqui.
        if (!isOwnWriteEcho) {
        const _ps2 =
          effectivePieceKey && data?.organizerStates?.[effectivePieceKey];
        // FIX C (03/09/2026, RELATORIO_DIAGNOSTICO_LOOP_INFINITO_TROCA_PECA,
        // Camada 5): este fallback pro campo legado (nível do caderno) não
        // tinha a mesma trava "só com 1 peça" que a branch "1ª vez" já
        // usa (ver legacyRecoveryElegivel mais abaixo neste arquivo). Sem
        // ela, toda vez que o campo por-peça ainda não existisse (peça
        // nova, antes do 1º save), o merge reintroduzia dado da peça 1 —
        // e a escrita de volta disparava o listener de novo (Camada 1),
        // fechando o ciclo. Mesma peça 1 nunca entra no loop porque o
        // campo por-peça dela já existe e já é idêntico ao legado.
        const totalPiecesForHydrateFallback = Array.isArray(data?.pieces)
          ? data.pieces.length
          : 0;
        const legacyFallbackElegivel = totalPiecesForHydrateFallback <= 1;
        const remoteSlotOrder = _ps2?.slotOrder
          ? _ps2.slotOrder
          : legacyFallbackElegivel
            ? data?.organizerState?.slotOrder
            : undefined;
        const remoteSlotNames = _ps2?.slotNames
          ? _ps2.slotNames
          : legacyFallbackElegivel
            ? data?.organizerState?.slotNames
            : undefined;
        const remoteClientReq = _ps2?.clientRequested
          ? _ps2.clientRequested
          : legacyFallbackElegivel
            ? data?.organizerState?.clientRequested
            : undefined;
        if (Array.isArray(remoteSlotOrder) && remoteSlotOrder.length > 0) {
          setSlotOrder((localOrder) => {
            const localSet = new Set(
              Array.isArray(localOrder) ? localOrder : [],
            );
            const newSlots = remoteSlotOrder.filter(
              (sid) => !localSet.has(sid),
            );
            if (newSlots.length === 0) return localOrder;
            let result = [...(localOrder || [])];
            for (const ns of newSlots) {
              const remoteIdx = remoteSlotOrder.indexOf(ns);
              let insertAt = result.length;
              for (let i = remoteIdx - 1; i >= 0; i--) {
                const prevSlot = remoteSlotOrder[i];
                const localPos = result.indexOf(prevSlot);
                if (localPos >= 0) {
                  insertAt = localPos + 1;
                  break;
                }
              }
              result = [
                ...result.slice(0, insertAt),
                ns,
                ...result.slice(insertAt),
              ];
            }
            return result;
          });
          if (remoteSlotNames && typeof remoteSlotNames === "object") {
            setSlotNames((localNames) => {
              let changed = false;
              const merged = { ...(localNames || {}) };
              for (const [sid, name] of Object.entries(remoteSlotNames)) {
                const isProcuracao = String(sid).startsWith("procuracao-");
                if (
                  name &&
                  (!merged[sid] || isProcuracao) &&
                  merged[sid] !== name
                ) {
                  merged[sid] = name;
                  changed = true;
                }
              }
              return changed ? merged : localNames;
            });
          }
          if (remoteClientReq && typeof remoteClientReq === "object") {
            setClientRequested((localReq) => {
              let changed = false;
              const merged = { ...(localReq || {}) };
              for (const [sid, val] of Object.entries(remoteClientReq)) {
                if (merged[sid] === undefined) {
                  merged[sid] = val;
                  changed = true;
                }
              }
              return changed ? merged : localReq;
            });
          }
        }
        }
      },
      () => {
        workspaceHydratedKeyRef.current = workspaceKey;
        setWorkspaceHydratedKey(workspaceKey);
        setHydratedWorkspaceKeyStore(workspaceKey);
      },
    );

    return () => unsub();
  }, [workspaceDocRef, workspaceKey, effectivePieceKey]);

  return {
    pendingAssign,
    setPendingAssign,
    syncBusy,
    setSyncBusy,
    chaosBusy,
    setChaosBusy,
    hostMode,
    setHostMode,
    uiWidthMode,
    setUiWidthMode,
    workspaceHydratedKey,
    setWorkspaceHydratedKey,
    isRenaming,
    setIsRenaming,
    renameProgress,
    isEdgeProcessing,
    setIsEdgeProcessing,
    uploadLocks,
    setUploadLocks,
    workspacePartyRefs,
    setWorkspacePartyRefs,
    partyDetails,
    setPartyDetails,
    pieceType,
    setPieceType,
    billingModal,
    setBillingModal,
    nukeOpen,
    setNukeOpen,
    nukeText,
    setNukeText,
    driveFolderInput,
    setDriveFolderInput,
    isMirrorMode,
    systemInitialFolderId,
    zeroClickActive,
    isDev,
    workspaceKey,
    workspaceDocRef,
    effectivePieceKey,
    stateKey,
    currentRamoDocs,
    assignedIds,
    orderedDriveFiles,
    pendingDriveFiles,
    selectedDriveFile,
    completion,
    norm,
    resolveClientName,
    resolveWorkspaceLabel,
    handleAIAnalyze,
    handleAIToggleItem,
    handleAIInjectSlots,
    loadClientsCache,
    loadWorkspacesCache,
    runClientSearch,
    runLinkSearch,
    pushNotice,
    updateSlotName,
    moveSlot,
    handleAddSlot,
    postBridgeState,
    handleSaveMetadata,
    applyPieceTypeLabel,
    renamePieceFolderOnDrive,
    autoFill,
    saveChecklistAsDefault,
    handleNukeWorkspace,
    handleCiteInWord,
    handleResetSlot,
    handleRemoveSlot,
    assignToSlot,
    applyPendingAssign,
    handleDragEnd,
    uploadAndAssign,
    renameSingleFile,
    applyRenameOnDrive,
    handleMasterSyncAll,
    commitPieceType,
    listDriveFolder,
    connectToDriveFolder,
    refreshDriveFiles,
    fetchDriveFolder,
    linkDriveFileToClient,
    linkDriveFileToWorkspace,
    handlePublishPetition,
    runPortalChaos,
    syncWorkspaceNow,

    drive,
    cache,
    search,
    partyPicker,
    checklist,
    meta,
    pinned,
    modals,
    fb,
    signatures,

    assignments,
    setAssignments,
    slotNames,
    setSlotNames,
    slotOrder,
    setSlotOrder,
    isHydrated,
    setHydratedState,
    resetOrganizerStore,
    clientRequested,
    setClientRequested,
    numeroInicialAnexos,
    setNumeroInicialAnexos,
    revertState,
    markHydrated,

    partyOmniboxRef,
    linkOmniboxRef,
    workspaceHydratedKeyRef,
    lastConfirmedOrgStateRef,
    metaJurisdictionRef,
    noticeTimerRef,
    renameTimerBySlotRef,
    driveListAbortRef,
    driveFolderInputRef,
    bridgePostTimerRef,
    autoConnectKeyRef,
    localOrganizerUpdatedAtRef,
  };
}
